/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  ActivateObjectsPathParameters,
  AssignObjectsToUsersPathParameters,
  CheckActiveCodePathParameters,
  CreateModLogPathParameters,
  DeleteRemedyPathParameters,
  GetFindingMetadataPathParameters,
  GetObjectsWithFindingsPathParameters,
  GetRelatedObjectsPathParameters,
  GetRemediationAnalysisStatusPathParameters,
  GetRemedyDetailParams,
  GetRemedyDetailPathParameters,
  GetScanFindingsPathParameters,
  GetSourceCodeParams,
  GetSourceCodePathParameters,
  ListRemediationSapObjectsParams,
  PerformSyntaxCheckPathParameters,
  PreviewPackageImportParams,
  PreviewTransportImportParams,
  ReconcileProgramFileSubobjectInsertParentTrViaAdtAsyncPathParameters,
  RemediateFlePathParameters,
  ScanPathParameters,
  TLActivationRequest,
  TLActivationResponse,
  TLApplyPseudocodeRequest,
  TLApplyPseudocodeResponse,
  TLAtcSingleScanRequest,
  TLAtcSingleScanResponse,
  TLCancelScansRequest,
  TLCodeRemediationV3SyntaxCheckRequest,
  TLCodeRemediationV3SyntaxCheckResponse,
  TLFindingMetadataResponse,
  TLFindingsListResponse,
  TLFleAnalyzeResponse,
  TLFLERemediateRequest,
  TLHTTPValidationError,
  TLModLogGenerationRequest,
  TLModLogGenerationResponse,
  TLObjectAssignmentRequest,
  TLObjectAssignmentResponse,
  TLObjectDetailsRequest,
  TLObjectIdsRequest,
  TLObjectIdsResponse,
  TLPackageImportCreateRequest,
  TLPackageImportPreviewRequest,
  TLPackageImportPreviewResponse,
  TLRemediationAnalysisJobResponse,
  TLRemediationAnalysisRequest,
  TLRemediationAnalysisStatusResponse,
  TLRemediationObjectImportRequest,
  TLRemediationObjectSearchResponse,
  TLRemedyCreateResponse,
  TLRemedyDetailResponse,
  TLRemedyListResponse,
  TLRemedyObjectsListResponse,
  TLRemedyResponse,
  TLRemedyTrBulkAcceptedResponse,
  TLRemedyTrBulkFromProgramRequest,
  TLRemedyUpdateRequest,
  TLRulesListResponse,
  TLScanRequest,
  TLScanResponse,
  TLSourceCodeResponse,
  TLSuccessResponse,
  TLTransportImportCreateRequest,
  TLTransportImportPreviewRequest,
  TLTransportImportPreviewResponse,
  TLUpdateFindingsStatusRequest,
  TLUpdateSourceCodeRequest,
  TLUpdateSourceCodeResponse,
  TLUpdateStatusResponse,
  TLValidateCodeRequest,
  TLValidateCodeResponse,
  UpdateFindingsStatusPathParameters,
  UpdateFindingStatusParams,
  UpdateFindingStatusPathParameters,
  UpdateObjectStatusParams,
  UpdateObjectStatusPathParameters,
  UpdateRemedyPathParameters,
  UpdateSourceCodePathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Apply Pseudocode
 */
export const applyPseudocode = (
  tLApplyPseudocodeRequest: BodyType<TLApplyPseudocodeRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLApplyPseudocodeResponse>(
    {
      url: `/code-remediation-v3/apply-pseudocode`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLApplyPseudocodeRequest,
      signal,
    },
    options,
  );
};

export const getApplyPseudocodeQueryKey = (tLApplyPseudocodeRequest?: BodyType<TLApplyPseudocodeRequest>) => {
  return ['POST', 'code-remediation-v3', 'apply-pseudocode', tLApplyPseudocodeRequest] as const;
};

export const getApplyPseudocodeQueryOptions = <
  TData = Awaited<ReturnType<typeof applyPseudocode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLApplyPseudocodeRequest: BodyType<TLApplyPseudocodeRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof applyPseudocode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getApplyPseudocodeQueryKey(tLApplyPseudocodeRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof applyPseudocode>>> = ({ signal }) =>
    applyPseudocode(tLApplyPseudocodeRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof applyPseudocode>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ApplyPseudocodeQueryResult = NonNullable<Awaited<ReturnType<typeof applyPseudocode>>>;
export type ApplyPseudocodeQueryError = ErrorType<TLHTTPValidationError>;

export function useApplyPseudocode<
  TData = Awaited<ReturnType<typeof applyPseudocode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLApplyPseudocodeRequest: BodyType<TLApplyPseudocodeRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof applyPseudocode>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof applyPseudocode>>,
          TError,
          Awaited<ReturnType<typeof applyPseudocode>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useApplyPseudocode<
  TData = Awaited<ReturnType<typeof applyPseudocode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLApplyPseudocodeRequest: BodyType<TLApplyPseudocodeRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof applyPseudocode>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof applyPseudocode>>,
          TError,
          Awaited<ReturnType<typeof applyPseudocode>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useApplyPseudocode<
  TData = Awaited<ReturnType<typeof applyPseudocode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLApplyPseudocodeRequest: BodyType<TLApplyPseudocodeRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof applyPseudocode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Apply Pseudocode
 */

export function useApplyPseudocode<
  TData = Awaited<ReturnType<typeof applyPseudocode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLApplyPseudocodeRequest: BodyType<TLApplyPseudocodeRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof applyPseudocode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getApplyPseudocodeQueryOptions(tLApplyPseudocodeRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Apply Pseudocode
 */
export const prefetchApplyPseudocodeQuery = async <
  TData = Awaited<ReturnType<typeof applyPseudocode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLApplyPseudocodeRequest: BodyType<TLApplyPseudocodeRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof applyPseudocode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getApplyPseudocodeQueryOptions(tLApplyPseudocodeRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Apply Pseudocode
 */
export const invalidateApplyPseudocode = async (
  queryClient: QueryClient,
  tLApplyPseudocodeRequest: BodyType<TLApplyPseudocodeRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getApplyPseudocodeQueryKey(tLApplyPseudocodeRequest) }, options);

  return queryClient;
};

/**
 * Run an ATC scan for a single ADT object and return findings inline.
 *
 * Used by the editor's "Run ATC" action — no DB persistence; the FE renders
 * the findings directly. Production code-remediation flows still go through
 * the remedy-scoped scan endpoint above.
 * @summary Run Single Object Atc Scan
 */
export const runSingleObjectAtcScan = (
  tLAtcSingleScanRequest: BodyType<TLAtcSingleScanRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLAtcSingleScanResponse>(
    {
      url: `/code-remediation-v3/atc-scan/single`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLAtcSingleScanRequest,
      signal,
    },
    options,
  );
};

export const getRunSingleObjectAtcScanQueryKey = (tLAtcSingleScanRequest?: BodyType<TLAtcSingleScanRequest>) => {
  return ['POST', 'code-remediation-v3', 'atc-scan', 'single', tLAtcSingleScanRequest] as const;
};

export const getRunSingleObjectAtcScanQueryOptions = <
  TData = Awaited<ReturnType<typeof runSingleObjectAtcScan>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLAtcSingleScanRequest: BodyType<TLAtcSingleScanRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof runSingleObjectAtcScan>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRunSingleObjectAtcScanQueryKey(tLAtcSingleScanRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof runSingleObjectAtcScan>>> = ({ signal }) =>
    runSingleObjectAtcScan(tLAtcSingleScanRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof runSingleObjectAtcScan>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type RunSingleObjectAtcScanQueryResult = NonNullable<Awaited<ReturnType<typeof runSingleObjectAtcScan>>>;
export type RunSingleObjectAtcScanQueryError = ErrorType<TLHTTPValidationError>;

export function useRunSingleObjectAtcScan<
  TData = Awaited<ReturnType<typeof runSingleObjectAtcScan>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLAtcSingleScanRequest: BodyType<TLAtcSingleScanRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof runSingleObjectAtcScan>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof runSingleObjectAtcScan>>,
          TError,
          Awaited<ReturnType<typeof runSingleObjectAtcScan>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRunSingleObjectAtcScan<
  TData = Awaited<ReturnType<typeof runSingleObjectAtcScan>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLAtcSingleScanRequest: BodyType<TLAtcSingleScanRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof runSingleObjectAtcScan>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof runSingleObjectAtcScan>>,
          TError,
          Awaited<ReturnType<typeof runSingleObjectAtcScan>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRunSingleObjectAtcScan<
  TData = Awaited<ReturnType<typeof runSingleObjectAtcScan>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLAtcSingleScanRequest: BodyType<TLAtcSingleScanRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof runSingleObjectAtcScan>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Run Single Object Atc Scan
 */

export function useRunSingleObjectAtcScan<
  TData = Awaited<ReturnType<typeof runSingleObjectAtcScan>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLAtcSingleScanRequest: BodyType<TLAtcSingleScanRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof runSingleObjectAtcScan>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRunSingleObjectAtcScanQueryOptions(tLAtcSingleScanRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Run Single Object Atc Scan
 */
export const prefetchRunSingleObjectAtcScanQuery = async <
  TData = Awaited<ReturnType<typeof runSingleObjectAtcScan>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLAtcSingleScanRequest: BodyType<TLAtcSingleScanRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof runSingleObjectAtcScan>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRunSingleObjectAtcScanQueryOptions(tLAtcSingleScanRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Run Single Object Atc Scan
 */
export const invalidateRunSingleObjectAtcScan = async (
  queryClient: QueryClient,
  tLAtcSingleScanRequest: BodyType<TLAtcSingleScanRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getRunSingleObjectAtcScanQueryKey(tLAtcSingleScanRequest) }, options);

  return queryClient;
};

/**
 * @summary Get Fle Rules
 */
export const getFleRules = (options?: SecondParameter<typeof apiInstance>, signal?: AbortSignal) => {
  return apiInstance<TLRulesListResponse>({ url: `/code-remediation-v3/fle-rules`, method: 'GET', signal }, options);
};

export const getGetFleRulesQueryKey = () => {
  return ['code-remediation-v3', 'fle-rules'] as const;
};

export const getGetFleRulesQueryOptions = <
  TData = Awaited<ReturnType<typeof getFleRules>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFleRules>>, TError, TData>>;
  request?: SecondParameter<typeof apiInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetFleRulesQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getFleRules>>> = ({ signal }) =>
    getFleRules(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getFleRules>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetFleRulesQueryResult = NonNullable<Awaited<ReturnType<typeof getFleRules>>>;
export type GetFleRulesQueryError = ErrorType<unknown>;

export function useGetFleRules<TData = Awaited<ReturnType<typeof getFleRules>>, TError = ErrorType<unknown>>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFleRules>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFleRules>>,
          TError,
          Awaited<ReturnType<typeof getFleRules>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetFleRules<TData = Awaited<ReturnType<typeof getFleRules>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFleRules>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFleRules>>,
          TError,
          Awaited<ReturnType<typeof getFleRules>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetFleRules<TData = Awaited<ReturnType<typeof getFleRules>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFleRules>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Fle Rules
 */

export function useGetFleRules<TData = Awaited<ReturnType<typeof getFleRules>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFleRules>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetFleRulesQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Fle Rules
 */
export const prefetchGetFleRulesQuery = async <
  TData = Awaited<ReturnType<typeof getFleRules>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFleRules>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetFleRulesQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Fle Rules
 */
export const invalidateGetFleRules = async (
  queryClient: QueryClient,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetFleRulesQueryKey() }, options);

  return queryClient;
};

/**
 * @summary Get Scan Findings
 */
export const getScanFindings = (
  { objectId, scanId }: GetScanFindingsPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLFindingsListResponse>(
    { url: `/code-remediation-v3/objects/${objectId}/scans/${scanId}/findings`, method: 'GET', signal },
    options,
  );
};

export const getGetScanFindingsQueryKey = ({ objectId, scanId }: GetScanFindingsPathParameters) => {
  return ['code-remediation-v3', 'objects', objectId, 'scans', scanId, 'findings'] as const;
};

export const getGetScanFindingsQueryOptions = <
  TData = Awaited<ReturnType<typeof getScanFindings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { objectId, scanId }: GetScanFindingsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getScanFindings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetScanFindingsQueryKey({ objectId, scanId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getScanFindings>>> = ({ signal }) =>
    getScanFindings({ objectId, scanId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: objectId !== null && objectId !== undefined && scanId !== null && scanId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getScanFindings>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetScanFindingsQueryResult = NonNullable<Awaited<ReturnType<typeof getScanFindings>>>;
export type GetScanFindingsQueryError = ErrorType<TLHTTPValidationError>;

export function useGetScanFindings<
  TData = Awaited<ReturnType<typeof getScanFindings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetScanFindingsPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getScanFindings>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getScanFindings>>,
          TError,
          Awaited<ReturnType<typeof getScanFindings>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetScanFindings<
  TData = Awaited<ReturnType<typeof getScanFindings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetScanFindingsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getScanFindings>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getScanFindings>>,
          TError,
          Awaited<ReturnType<typeof getScanFindings>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetScanFindings<
  TData = Awaited<ReturnType<typeof getScanFindings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetScanFindingsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getScanFindings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Scan Findings
 */

export function useGetScanFindings<
  TData = Awaited<ReturnType<typeof getScanFindings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { objectId, scanId }: GetScanFindingsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getScanFindings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetScanFindingsQueryOptions({ objectId, scanId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Scan Findings
 */
export const prefetchGetScanFindingsQuery = async <
  TData = Awaited<ReturnType<typeof getScanFindings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { objectId, scanId }: GetScanFindingsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getScanFindings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetScanFindingsQueryOptions({ objectId, scanId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Scan Findings
 */
export const invalidateGetScanFindings = async (
  queryClient: QueryClient,
  { objectId, scanId }: GetScanFindingsPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetScanFindingsQueryKey({ objectId, scanId }) }, options);

  return queryClient;
};

/**
 * @summary Update Findings Status
 */
export const updateFindingsStatus = (
  { objectId, scanId }: UpdateFindingsStatusPathParameters,
  tLUpdateFindingsStatusRequest: BodyType<TLUpdateFindingsStatusRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    {
      url: `/code-remediation-v3/objects/${objectId}/scans/${scanId}/findings/status`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLUpdateFindingsStatusRequest,
      signal,
    },
    options,
  );
};

export const getUpdateFindingsStatusQueryKey = (
  { objectId, scanId }: UpdateFindingsStatusPathParameters,
  tLUpdateFindingsStatusRequest?: BodyType<TLUpdateFindingsStatusRequest>,
) => {
  return [
    'PATCH',
    'code-remediation-v3',
    'objects',
    objectId,
    'scans',
    scanId,
    'findings',
    'status',
    tLUpdateFindingsStatusRequest,
  ] as const;
};

export const getUpdateFindingsStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof updateFindingsStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { objectId, scanId }: UpdateFindingsStatusPathParameters,
  tLUpdateFindingsStatusRequest: BodyType<TLUpdateFindingsStatusRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateFindingsStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getUpdateFindingsStatusQueryKey({ objectId, scanId }, tLUpdateFindingsStatusRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateFindingsStatus>>> = ({ signal }) =>
    updateFindingsStatus({ objectId, scanId }, tLUpdateFindingsStatusRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: objectId !== null && objectId !== undefined && scanId !== null && scanId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateFindingsStatus>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateFindingsStatusQueryResult = NonNullable<Awaited<ReturnType<typeof updateFindingsStatus>>>;
export type UpdateFindingsStatusQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateFindingsStatus<
  TData = Awaited<ReturnType<typeof updateFindingsStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateFindingsStatusPathParameters,
  tLUpdateFindingsStatusRequest: BodyType<TLUpdateFindingsStatusRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateFindingsStatus>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateFindingsStatus>>,
          TError,
          Awaited<ReturnType<typeof updateFindingsStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateFindingsStatus<
  TData = Awaited<ReturnType<typeof updateFindingsStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateFindingsStatusPathParameters,
  tLUpdateFindingsStatusRequest: BodyType<TLUpdateFindingsStatusRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateFindingsStatus>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateFindingsStatus>>,
          TError,
          Awaited<ReturnType<typeof updateFindingsStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateFindingsStatus<
  TData = Awaited<ReturnType<typeof updateFindingsStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateFindingsStatusPathParameters,
  tLUpdateFindingsStatusRequest: BodyType<TLUpdateFindingsStatusRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateFindingsStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Findings Status
 */

export function useUpdateFindingsStatus<
  TData = Awaited<ReturnType<typeof updateFindingsStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { objectId, scanId }: UpdateFindingsStatusPathParameters,
  tLUpdateFindingsStatusRequest: BodyType<TLUpdateFindingsStatusRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateFindingsStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateFindingsStatusQueryOptions(
    { objectId, scanId },
    tLUpdateFindingsStatusRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Findings Status
 */
export const prefetchUpdateFindingsStatusQuery = async <
  TData = Awaited<ReturnType<typeof updateFindingsStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { objectId, scanId }: UpdateFindingsStatusPathParameters,
  tLUpdateFindingsStatusRequest: BodyType<TLUpdateFindingsStatusRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateFindingsStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateFindingsStatusQueryOptions(
    { objectId, scanId },
    tLUpdateFindingsStatusRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Findings Status
 */
export const invalidateUpdateFindingsStatus = async (
  queryClient: QueryClient,
  { objectId, scanId }: UpdateFindingsStatusPathParameters,
  tLUpdateFindingsStatusRequest: BodyType<TLUpdateFindingsStatusRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateFindingsStatusQueryKey({ objectId, scanId }, tLUpdateFindingsStatusRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Update Finding Status
 */
export const updateFindingStatus = (
  { objectId, scanId, findingId }: UpdateFindingStatusPathParameters,
  params: UpdateFindingStatusParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    {
      url: `/code-remediation-v3/objects/${objectId}/scans/${scanId}/findings/${findingId}/status`,
      method: 'PATCH',
      params,
      signal,
    },
    options,
  );
};

export const getUpdateFindingStatusQueryKey = (
  { objectId, scanId, findingId }: UpdateFindingStatusPathParameters,
  params?: UpdateFindingStatusParams,
) => {
  return [
    'PATCH',
    'code-remediation-v3',
    'objects',
    objectId,
    'scans',
    scanId,
    'findings',
    findingId,
    'status',
    ...(params ? [params] : []),
  ] as const;
};

export const getUpdateFindingStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof updateFindingStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { objectId, scanId, findingId }: UpdateFindingStatusPathParameters,
  params: UpdateFindingStatusParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateFindingStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUpdateFindingStatusQueryKey({ objectId, scanId, findingId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateFindingStatus>>> = ({ signal }) =>
    updateFindingStatus({ objectId, scanId, findingId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled:
      objectId !== null &&
      objectId !== undefined &&
      scanId !== null &&
      scanId !== undefined &&
      findingId !== null &&
      findingId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateFindingStatus>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateFindingStatusQueryResult = NonNullable<Awaited<ReturnType<typeof updateFindingStatus>>>;
export type UpdateFindingStatusQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateFindingStatus<
  TData = Awaited<ReturnType<typeof updateFindingStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateFindingStatusPathParameters,
  params: UpdateFindingStatusParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateFindingStatus>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateFindingStatus>>,
          TError,
          Awaited<ReturnType<typeof updateFindingStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateFindingStatus<
  TData = Awaited<ReturnType<typeof updateFindingStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateFindingStatusPathParameters,
  params: UpdateFindingStatusParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateFindingStatus>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateFindingStatus>>,
          TError,
          Awaited<ReturnType<typeof updateFindingStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateFindingStatus<
  TData = Awaited<ReturnType<typeof updateFindingStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateFindingStatusPathParameters,
  params: UpdateFindingStatusParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateFindingStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Finding Status
 */

export function useUpdateFindingStatus<
  TData = Awaited<ReturnType<typeof updateFindingStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { objectId, scanId, findingId }: UpdateFindingStatusPathParameters,
  params: UpdateFindingStatusParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateFindingStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateFindingStatusQueryOptions({ objectId, scanId, findingId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Finding Status
 */
export const prefetchUpdateFindingStatusQuery = async <
  TData = Awaited<ReturnType<typeof updateFindingStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { objectId, scanId, findingId }: UpdateFindingStatusPathParameters,
  params: UpdateFindingStatusParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateFindingStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateFindingStatusQueryOptions({ objectId, scanId, findingId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Finding Status
 */
export const invalidateUpdateFindingStatus = async (
  queryClient: QueryClient,
  { objectId, scanId, findingId }: UpdateFindingStatusPathParameters,
  params: UpdateFindingStatusParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateFindingStatusQueryKey({ objectId, scanId, findingId }, params) },
    options,
  );

  return queryClient;
};

/**
 * @summary Create Package Import
 */
export const createPackageImport = (
  tLPackageImportCreateRequest: BodyType<TLPackageImportCreateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLRemedyCreateResponse>(
    {
      url: `/code-remediation-v3/package-imports`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLPackageImportCreateRequest,
      signal,
    },
    options,
  );
};

export const getCreatePackageImportQueryKey = (
  tLPackageImportCreateRequest?: BodyType<TLPackageImportCreateRequest>,
) => {
  return ['POST', 'code-remediation-v3', 'package-imports', tLPackageImportCreateRequest] as const;
};

export const getCreatePackageImportQueryOptions = <
  TData = Awaited<ReturnType<typeof createPackageImport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLPackageImportCreateRequest: BodyType<TLPackageImportCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createPackageImport>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreatePackageImportQueryKey(tLPackageImportCreateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createPackageImport>>> = ({ signal }) =>
    createPackageImport(tLPackageImportCreateRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createPackageImport>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreatePackageImportQueryResult = NonNullable<Awaited<ReturnType<typeof createPackageImport>>>;
export type CreatePackageImportQueryError = ErrorType<TLHTTPValidationError>;

export function useCreatePackageImport<
  TData = Awaited<ReturnType<typeof createPackageImport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLPackageImportCreateRequest: BodyType<TLPackageImportCreateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createPackageImport>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createPackageImport>>,
          TError,
          Awaited<ReturnType<typeof createPackageImport>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreatePackageImport<
  TData = Awaited<ReturnType<typeof createPackageImport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLPackageImportCreateRequest: BodyType<TLPackageImportCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createPackageImport>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createPackageImport>>,
          TError,
          Awaited<ReturnType<typeof createPackageImport>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreatePackageImport<
  TData = Awaited<ReturnType<typeof createPackageImport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLPackageImportCreateRequest: BodyType<TLPackageImportCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createPackageImport>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Package Import
 */

export function useCreatePackageImport<
  TData = Awaited<ReturnType<typeof createPackageImport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLPackageImportCreateRequest: BodyType<TLPackageImportCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createPackageImport>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreatePackageImportQueryOptions(tLPackageImportCreateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Package Import
 */
export const prefetchCreatePackageImportQuery = async <
  TData = Awaited<ReturnType<typeof createPackageImport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLPackageImportCreateRequest: BodyType<TLPackageImportCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createPackageImport>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreatePackageImportQueryOptions(tLPackageImportCreateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Package Import
 */
export const invalidateCreatePackageImport = async (
  queryClient: QueryClient,
  tLPackageImportCreateRequest: BodyType<TLPackageImportCreateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getCreatePackageImportQueryKey(tLPackageImportCreateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Preview Package Import
 */
export const previewPackageImport = (
  tLPackageImportPreviewRequest: BodyType<TLPackageImportPreviewRequest>,
  params?: PreviewPackageImportParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPackageImportPreviewResponse>(
    {
      url: `/code-remediation-v3/package-imports/preview`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLPackageImportPreviewRequest,
      params,
      signal,
    },
    options,
  );
};

export const getPreviewPackageImportQueryKey = (
  tLPackageImportPreviewRequest?: BodyType<TLPackageImportPreviewRequest>,
  params?: PreviewPackageImportParams,
) => {
  return [
    'POST',
    'code-remediation-v3',
    'package-imports',
    'preview',
    ...(params ? [params] : []),
    tLPackageImportPreviewRequest,
  ] as const;
};

export const getPreviewPackageImportQueryOptions = <
  TData = Awaited<ReturnType<typeof previewPackageImport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLPackageImportPreviewRequest: BodyType<TLPackageImportPreviewRequest>,
  params?: PreviewPackageImportParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewPackageImport>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getPreviewPackageImportQueryKey(tLPackageImportPreviewRequest, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof previewPackageImport>>> = ({ signal }) =>
    previewPackageImport(tLPackageImportPreviewRequest, params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof previewPackageImport>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PreviewPackageImportQueryResult = NonNullable<Awaited<ReturnType<typeof previewPackageImport>>>;
export type PreviewPackageImportQueryError = ErrorType<TLHTTPValidationError>;

export function usePreviewPackageImport<
  TData = Awaited<ReturnType<typeof previewPackageImport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLPackageImportPreviewRequest: BodyType<TLPackageImportPreviewRequest>,
  params: undefined | PreviewPackageImportParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewPackageImport>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof previewPackageImport>>,
          TError,
          Awaited<ReturnType<typeof previewPackageImport>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePreviewPackageImport<
  TData = Awaited<ReturnType<typeof previewPackageImport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLPackageImportPreviewRequest: BodyType<TLPackageImportPreviewRequest>,
  params?: PreviewPackageImportParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewPackageImport>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof previewPackageImport>>,
          TError,
          Awaited<ReturnType<typeof previewPackageImport>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePreviewPackageImport<
  TData = Awaited<ReturnType<typeof previewPackageImport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLPackageImportPreviewRequest: BodyType<TLPackageImportPreviewRequest>,
  params?: PreviewPackageImportParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewPackageImport>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Preview Package Import
 */

export function usePreviewPackageImport<
  TData = Awaited<ReturnType<typeof previewPackageImport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLPackageImportPreviewRequest: BodyType<TLPackageImportPreviewRequest>,
  params?: PreviewPackageImportParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewPackageImport>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getPreviewPackageImportQueryOptions(tLPackageImportPreviewRequest, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Preview Package Import
 */
export const prefetchPreviewPackageImportQuery = async <
  TData = Awaited<ReturnType<typeof previewPackageImport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLPackageImportPreviewRequest: BodyType<TLPackageImportPreviewRequest>,
  params?: PreviewPackageImportParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewPackageImport>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getPreviewPackageImportQueryOptions(tLPackageImportPreviewRequest, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Preview Package Import
 */
export const invalidatePreviewPackageImport = async (
  queryClient: QueryClient,
  tLPackageImportPreviewRequest: BodyType<TLPackageImportPreviewRequest>,
  params?: PreviewPackageImportParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getPreviewPackageImportQueryKey(tLPackageImportPreviewRequest, params) },
    options,
  );

  return queryClient;
};

/**
 * Start an async export of every object/subobject under the named remedy.
 *
 * The background task generates an xlsx (object hierarchy + LLM-summarized
 * Tessera-AI markers per subobject), uploads it to storage, and updates the
 * returned job row to COMPLETED. Poll `GET /remediation-analysis/{job_id}`
 * to retrieve the presigned download URL.
 * @summary Start Remediation Analysis
 */
export const startRemediationAnalysis = (
  tLRemediationAnalysisRequest: BodyType<TLRemediationAnalysisRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLRemediationAnalysisJobResponse>(
    {
      url: `/code-remediation-v3/remediation-analysis`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLRemediationAnalysisRequest,
      signal,
    },
    options,
  );
};

export const getStartRemediationAnalysisQueryKey = (
  tLRemediationAnalysisRequest?: BodyType<TLRemediationAnalysisRequest>,
) => {
  return ['POST', 'code-remediation-v3', 'remediation-analysis', tLRemediationAnalysisRequest] as const;
};

export const getStartRemediationAnalysisQueryOptions = <
  TData = Awaited<ReturnType<typeof startRemediationAnalysis>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLRemediationAnalysisRequest: BodyType<TLRemediationAnalysisRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof startRemediationAnalysis>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getStartRemediationAnalysisQueryKey(tLRemediationAnalysisRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof startRemediationAnalysis>>> = ({ signal }) =>
    startRemediationAnalysis(tLRemediationAnalysisRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof startRemediationAnalysis>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type StartRemediationAnalysisQueryResult = NonNullable<Awaited<ReturnType<typeof startRemediationAnalysis>>>;
export type StartRemediationAnalysisQueryError = ErrorType<TLHTTPValidationError>;

export function useStartRemediationAnalysis<
  TData = Awaited<ReturnType<typeof startRemediationAnalysis>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLRemediationAnalysisRequest: BodyType<TLRemediationAnalysisRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof startRemediationAnalysis>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof startRemediationAnalysis>>,
          TError,
          Awaited<ReturnType<typeof startRemediationAnalysis>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useStartRemediationAnalysis<
  TData = Awaited<ReturnType<typeof startRemediationAnalysis>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLRemediationAnalysisRequest: BodyType<TLRemediationAnalysisRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof startRemediationAnalysis>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof startRemediationAnalysis>>,
          TError,
          Awaited<ReturnType<typeof startRemediationAnalysis>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useStartRemediationAnalysis<
  TData = Awaited<ReturnType<typeof startRemediationAnalysis>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLRemediationAnalysisRequest: BodyType<TLRemediationAnalysisRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof startRemediationAnalysis>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Start Remediation Analysis
 */

export function useStartRemediationAnalysis<
  TData = Awaited<ReturnType<typeof startRemediationAnalysis>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLRemediationAnalysisRequest: BodyType<TLRemediationAnalysisRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof startRemediationAnalysis>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getStartRemediationAnalysisQueryOptions(tLRemediationAnalysisRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Start Remediation Analysis
 */
export const prefetchStartRemediationAnalysisQuery = async <
  TData = Awaited<ReturnType<typeof startRemediationAnalysis>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLRemediationAnalysisRequest: BodyType<TLRemediationAnalysisRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof startRemediationAnalysis>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getStartRemediationAnalysisQueryOptions(tLRemediationAnalysisRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Start Remediation Analysis
 */
export const invalidateStartRemediationAnalysis = async (
  queryClient: QueryClient,
  tLRemediationAnalysisRequest: BodyType<TLRemediationAnalysisRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getStartRemediationAnalysisQueryKey(tLRemediationAnalysisRequest) },
    options,
  );

  return queryClient;
};

/**
 * Poll an in-flight or completed remediation-analysis job.
 *
 * Returns a presigned `download_url` (valid for one hour) when the job is
 * COMPLETED. FAILED jobs include the error message.
 * @summary Get Remediation Analysis Status
 */
export const getRemediationAnalysisStatus = (
  { jobId }: GetRemediationAnalysisStatusPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLRemediationAnalysisStatusResponse>(
    { url: `/code-remediation-v3/remediation-analysis/${jobId}`, method: 'GET', signal },
    options,
  );
};

export const getGetRemediationAnalysisStatusQueryKey = ({ jobId }: GetRemediationAnalysisStatusPathParameters) => {
  return ['code-remediation-v3', 'remediation-analysis', jobId] as const;
};

export const getGetRemediationAnalysisStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof getRemediationAnalysisStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { jobId }: GetRemediationAnalysisStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRemediationAnalysisStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetRemediationAnalysisStatusQueryKey({ jobId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getRemediationAnalysisStatus>>> = ({ signal }) =>
    getRemediationAnalysisStatus({ jobId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: jobId !== null && jobId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getRemediationAnalysisStatus>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetRemediationAnalysisStatusQueryResult = NonNullable<
  Awaited<ReturnType<typeof getRemediationAnalysisStatus>>
>;
export type GetRemediationAnalysisStatusQueryError = ErrorType<TLHTTPValidationError>;

export function useGetRemediationAnalysisStatus<
  TData = Awaited<ReturnType<typeof getRemediationAnalysisStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetRemediationAnalysisStatusPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRemediationAnalysisStatus>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getRemediationAnalysisStatus>>,
          TError,
          Awaited<ReturnType<typeof getRemediationAnalysisStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetRemediationAnalysisStatus<
  TData = Awaited<ReturnType<typeof getRemediationAnalysisStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetRemediationAnalysisStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRemediationAnalysisStatus>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getRemediationAnalysisStatus>>,
          TError,
          Awaited<ReturnType<typeof getRemediationAnalysisStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetRemediationAnalysisStatus<
  TData = Awaited<ReturnType<typeof getRemediationAnalysisStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetRemediationAnalysisStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRemediationAnalysisStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Remediation Analysis Status
 */

export function useGetRemediationAnalysisStatus<
  TData = Awaited<ReturnType<typeof getRemediationAnalysisStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { jobId }: GetRemediationAnalysisStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRemediationAnalysisStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetRemediationAnalysisStatusQueryOptions({ jobId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Remediation Analysis Status
 */
export const prefetchGetRemediationAnalysisStatusQuery = async <
  TData = Awaited<ReturnType<typeof getRemediationAnalysisStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { jobId }: GetRemediationAnalysisStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRemediationAnalysisStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetRemediationAnalysisStatusQueryOptions({ jobId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Remediation Analysis Status
 */
export const invalidateGetRemediationAnalysisStatus = async (
  queryClient: QueryClient,
  { jobId }: GetRemediationAnalysisStatusPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetRemediationAnalysisStatusQueryKey({ jobId }) }, options);

  return queryClient;
};

/**
 * @summary Get All Remedies
 */
export const getAllRemedies = (options?: SecondParameter<typeof apiInstance>, signal?: AbortSignal) => {
  return apiInstance<TLRemedyListResponse>({ url: `/code-remediation-v3/remedies`, method: 'GET', signal }, options);
};

export const getGetAllRemediesQueryKey = () => {
  return ['code-remediation-v3', 'remedies'] as const;
};

export const getGetAllRemediesQueryOptions = <
  TData = Awaited<ReturnType<typeof getAllRemedies>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllRemedies>>, TError, TData>>;
  request?: SecondParameter<typeof apiInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAllRemediesQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllRemedies>>> = ({ signal }) =>
    getAllRemedies(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAllRemedies>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAllRemediesQueryResult = NonNullable<Awaited<ReturnType<typeof getAllRemedies>>>;
export type GetAllRemediesQueryError = ErrorType<unknown>;

export function useGetAllRemedies<TData = Awaited<ReturnType<typeof getAllRemedies>>, TError = ErrorType<unknown>>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllRemedies>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllRemedies>>,
          TError,
          Awaited<ReturnType<typeof getAllRemedies>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllRemedies<TData = Awaited<ReturnType<typeof getAllRemedies>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllRemedies>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllRemedies>>,
          TError,
          Awaited<ReturnType<typeof getAllRemedies>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllRemedies<TData = Awaited<ReturnType<typeof getAllRemedies>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllRemedies>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get All Remedies
 */

export function useGetAllRemedies<TData = Awaited<ReturnType<typeof getAllRemedies>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllRemedies>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAllRemediesQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get All Remedies
 */
export const prefetchGetAllRemediesQuery = async <
  TData = Awaited<ReturnType<typeof getAllRemedies>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllRemedies>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetAllRemediesQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get All Remedies
 */
export const invalidateGetAllRemedies = async (
  queryClient: QueryClient,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetAllRemediesQueryKey() }, options);

  return queryClient;
};

/**
 * @summary Delete Remedy
 */
export const deleteRemedy = (
  { remedyId }: DeleteRemedyPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<void>({ url: `/code-remediation-v3/remedies/${remedyId}`, method: 'DELETE', signal }, options);
};

export const getDeleteRemedyQueryKey = ({ remedyId }: DeleteRemedyPathParameters) => {
  return ['DELETE', 'code-remediation-v3', 'remedies', remedyId] as const;
};

export const getDeleteRemedyQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteRemedy>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId }: DeleteRemedyPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteRemedy>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteRemedyQueryKey({ remedyId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteRemedy>>> = ({ signal }) =>
    deleteRemedy({ remedyId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: remedyId !== null && remedyId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof deleteRemedy>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DeleteRemedyQueryResult = NonNullable<Awaited<ReturnType<typeof deleteRemedy>>>;
export type DeleteRemedyQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteRemedy<
  TData = Awaited<ReturnType<typeof deleteRemedy>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteRemedyPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteRemedy>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteRemedy>>,
          TError,
          Awaited<ReturnType<typeof deleteRemedy>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteRemedy<
  TData = Awaited<ReturnType<typeof deleteRemedy>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteRemedyPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteRemedy>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteRemedy>>,
          TError,
          Awaited<ReturnType<typeof deleteRemedy>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteRemedy<
  TData = Awaited<ReturnType<typeof deleteRemedy>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteRemedyPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteRemedy>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Remedy
 */

export function useDeleteRemedy<
  TData = Awaited<ReturnType<typeof deleteRemedy>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId }: DeleteRemedyPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteRemedy>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteRemedyQueryOptions({ remedyId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Remedy
 */
export const prefetchDeleteRemedyQuery = async <
  TData = Awaited<ReturnType<typeof deleteRemedy>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { remedyId }: DeleteRemedyPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteRemedy>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteRemedyQueryOptions({ remedyId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Remedy
 */
export const invalidateDeleteRemedy = async (
  queryClient: QueryClient,
  { remedyId }: DeleteRemedyPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteRemedyQueryKey({ remedyId }) }, options);

  return queryClient;
};

/**
 * @summary Get Remedy Detail
 */
export const getRemedyDetail = (
  { remedyId }: GetRemedyDetailPathParameters,
  params?: GetRemedyDetailParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLRemedyDetailResponse>(
    { url: `/code-remediation-v3/remedies/${remedyId}`, method: 'GET', params, signal },
    options,
  );
};

export const getGetRemedyDetailQueryKey = (
  { remedyId }: GetRemedyDetailPathParameters,
  params?: GetRemedyDetailParams,
) => {
  return ['code-remediation-v3', 'remedies', remedyId, ...(params ? [params] : [])] as const;
};

export const getGetRemedyDetailQueryOptions = <
  TData = Awaited<ReturnType<typeof getRemedyDetail>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId }: GetRemedyDetailPathParameters,
  params?: GetRemedyDetailParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRemedyDetail>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetRemedyDetailQueryKey({ remedyId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getRemedyDetail>>> = ({ signal }) =>
    getRemedyDetail({ remedyId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: remedyId !== null && remedyId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getRemedyDetail>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetRemedyDetailQueryResult = NonNullable<Awaited<ReturnType<typeof getRemedyDetail>>>;
export type GetRemedyDetailQueryError = ErrorType<TLHTTPValidationError>;

export function useGetRemedyDetail<
  TData = Awaited<ReturnType<typeof getRemedyDetail>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetRemedyDetailPathParameters,
  params: undefined | GetRemedyDetailParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRemedyDetail>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getRemedyDetail>>,
          TError,
          Awaited<ReturnType<typeof getRemedyDetail>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetRemedyDetail<
  TData = Awaited<ReturnType<typeof getRemedyDetail>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetRemedyDetailPathParameters,
  params?: GetRemedyDetailParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRemedyDetail>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getRemedyDetail>>,
          TError,
          Awaited<ReturnType<typeof getRemedyDetail>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetRemedyDetail<
  TData = Awaited<ReturnType<typeof getRemedyDetail>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetRemedyDetailPathParameters,
  params?: GetRemedyDetailParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRemedyDetail>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Remedy Detail
 */

export function useGetRemedyDetail<
  TData = Awaited<ReturnType<typeof getRemedyDetail>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId }: GetRemedyDetailPathParameters,
  params?: GetRemedyDetailParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRemedyDetail>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetRemedyDetailQueryOptions({ remedyId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Remedy Detail
 */
export const prefetchGetRemedyDetailQuery = async <
  TData = Awaited<ReturnType<typeof getRemedyDetail>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { remedyId }: GetRemedyDetailPathParameters,
  params?: GetRemedyDetailParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRemedyDetail>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetRemedyDetailQueryOptions({ remedyId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Remedy Detail
 */
export const invalidateGetRemedyDetail = async (
  queryClient: QueryClient,
  { remedyId }: GetRemedyDetailPathParameters,
  params?: GetRemedyDetailParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetRemedyDetailQueryKey({ remedyId }, params) }, options);

  return queryClient;
};

/**
 * @summary Assign Objects To Users
 */
export const assignObjectsToUsers = (
  { remedyId }: AssignObjectsToUsersPathParameters,
  tLObjectAssignmentRequest: BodyType<TLObjectAssignmentRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLObjectAssignmentResponse>(
    {
      url: `/code-remediation-v3/remedies/${remedyId}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLObjectAssignmentRequest,
      signal,
    },
    options,
  );
};

export const getAssignObjectsToUsersQueryKey = (
  { remedyId }: AssignObjectsToUsersPathParameters,
  tLObjectAssignmentRequest?: BodyType<TLObjectAssignmentRequest>,
) => {
  return ['PATCH', 'code-remediation-v3', 'remedies', remedyId, tLObjectAssignmentRequest] as const;
};

export const getAssignObjectsToUsersQueryOptions = <
  TData = Awaited<ReturnType<typeof assignObjectsToUsers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId }: AssignObjectsToUsersPathParameters,
  tLObjectAssignmentRequest: BodyType<TLObjectAssignmentRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof assignObjectsToUsers>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getAssignObjectsToUsersQueryKey({ remedyId }, tLObjectAssignmentRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof assignObjectsToUsers>>> = ({ signal }) =>
    assignObjectsToUsers({ remedyId }, tLObjectAssignmentRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: remedyId !== null && remedyId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof assignObjectsToUsers>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type AssignObjectsToUsersQueryResult = NonNullable<Awaited<ReturnType<typeof assignObjectsToUsers>>>;
export type AssignObjectsToUsersQueryError = ErrorType<TLHTTPValidationError>;

export function useAssignObjectsToUsers<
  TData = Awaited<ReturnType<typeof assignObjectsToUsers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AssignObjectsToUsersPathParameters,
  tLObjectAssignmentRequest: BodyType<TLObjectAssignmentRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof assignObjectsToUsers>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof assignObjectsToUsers>>,
          TError,
          Awaited<ReturnType<typeof assignObjectsToUsers>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAssignObjectsToUsers<
  TData = Awaited<ReturnType<typeof assignObjectsToUsers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AssignObjectsToUsersPathParameters,
  tLObjectAssignmentRequest: BodyType<TLObjectAssignmentRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof assignObjectsToUsers>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof assignObjectsToUsers>>,
          TError,
          Awaited<ReturnType<typeof assignObjectsToUsers>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAssignObjectsToUsers<
  TData = Awaited<ReturnType<typeof assignObjectsToUsers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AssignObjectsToUsersPathParameters,
  tLObjectAssignmentRequest: BodyType<TLObjectAssignmentRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof assignObjectsToUsers>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Assign Objects To Users
 */

export function useAssignObjectsToUsers<
  TData = Awaited<ReturnType<typeof assignObjectsToUsers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId }: AssignObjectsToUsersPathParameters,
  tLObjectAssignmentRequest: BodyType<TLObjectAssignmentRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof assignObjectsToUsers>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getAssignObjectsToUsersQueryOptions({ remedyId }, tLObjectAssignmentRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Assign Objects To Users
 */
export const prefetchAssignObjectsToUsersQuery = async <
  TData = Awaited<ReturnType<typeof assignObjectsToUsers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { remedyId }: AssignObjectsToUsersPathParameters,
  tLObjectAssignmentRequest: BodyType<TLObjectAssignmentRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof assignObjectsToUsers>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getAssignObjectsToUsersQueryOptions({ remedyId }, tLObjectAssignmentRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Assign Objects To Users
 */
export const invalidateAssignObjectsToUsers = async (
  queryClient: QueryClient,
  { remedyId }: AssignObjectsToUsersPathParameters,
  tLObjectAssignmentRequest: BodyType<TLObjectAssignmentRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getAssignObjectsToUsersQueryKey({ remedyId }, tLObjectAssignmentRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Activate Objects
 */
export const activateObjects = (
  { remedyId }: ActivateObjectsPathParameters,
  tLActivationRequest: BodyType<TLActivationRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLActivationResponse>(
    {
      url: `/code-remediation-v3/remedies/${remedyId}/activate`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLActivationRequest,
      signal,
    },
    options,
  );
};

export const getActivateObjectsQueryKey = (
  { remedyId }: ActivateObjectsPathParameters,
  tLActivationRequest?: BodyType<TLActivationRequest>,
) => {
  return ['POST', 'code-remediation-v3', 'remedies', remedyId, 'activate', tLActivationRequest] as const;
};

export const getActivateObjectsQueryOptions = <
  TData = Awaited<ReturnType<typeof activateObjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId }: ActivateObjectsPathParameters,
  tLActivationRequest: BodyType<TLActivationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof activateObjects>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getActivateObjectsQueryKey({ remedyId }, tLActivationRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof activateObjects>>> = ({ signal }) =>
    activateObjects({ remedyId }, tLActivationRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: remedyId !== null && remedyId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof activateObjects>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ActivateObjectsQueryResult = NonNullable<Awaited<ReturnType<typeof activateObjects>>>;
export type ActivateObjectsQueryError = ErrorType<TLHTTPValidationError>;

export function useActivateObjects<
  TData = Awaited<ReturnType<typeof activateObjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ActivateObjectsPathParameters,
  tLActivationRequest: BodyType<TLActivationRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof activateObjects>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof activateObjects>>,
          TError,
          Awaited<ReturnType<typeof activateObjects>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useActivateObjects<
  TData = Awaited<ReturnType<typeof activateObjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ActivateObjectsPathParameters,
  tLActivationRequest: BodyType<TLActivationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof activateObjects>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof activateObjects>>,
          TError,
          Awaited<ReturnType<typeof activateObjects>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useActivateObjects<
  TData = Awaited<ReturnType<typeof activateObjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ActivateObjectsPathParameters,
  tLActivationRequest: BodyType<TLActivationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof activateObjects>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Activate Objects
 */

export function useActivateObjects<
  TData = Awaited<ReturnType<typeof activateObjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId }: ActivateObjectsPathParameters,
  tLActivationRequest: BodyType<TLActivationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof activateObjects>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getActivateObjectsQueryOptions({ remedyId }, tLActivationRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Activate Objects
 */
export const prefetchActivateObjectsQuery = async <
  TData = Awaited<ReturnType<typeof activateObjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { remedyId }: ActivateObjectsPathParameters,
  tLActivationRequest: BodyType<TLActivationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof activateObjects>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getActivateObjectsQueryOptions({ remedyId }, tLActivationRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Activate Objects
 */
export const invalidateActivateObjects = async (
  queryClient: QueryClient,
  { remedyId }: ActivateObjectsPathParameters,
  tLActivationRequest: BodyType<TLActivationRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getActivateObjectsQueryKey({ remedyId }, tLActivationRequest) },
    options,
  );

  return queryClient;
};

/**
 * Loads and parses the program file, sets remedy status to SETUP_IN_PROGRESS, then runs reconciliation in a background task (subobject insert, SAP transports, source refresh). Returns 202; poll GET /remedies/{remedy_id} for SETUP_COMPLETED or SETUP_FAILED. Detailed counts and row errors are logged server-side.
 * @summary Reconcile program file: subobject insert + parent TR via ADT (async)
 */
export const reconcileProgramFileSubobjectInsertParentTrViaAdtAsync = (
  { remedyId }: ReconcileProgramFileSubobjectInsertParentTrViaAdtAsyncPathParameters,
  tLRemedyTrBulkFromProgramRequest: BodyType<TLRemedyTrBulkFromProgramRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLRemedyTrBulkAcceptedResponse>(
    {
      url: `/code-remediation-v3/remedies/${remedyId}/bulk-transport-requests`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLRemedyTrBulkFromProgramRequest,
      signal,
    },
    options,
  );
};

export const getReconcileProgramFileSubobjectInsertParentTrViaAdtAsyncQueryKey = (
  { remedyId }: ReconcileProgramFileSubobjectInsertParentTrViaAdtAsyncPathParameters,
  tLRemedyTrBulkFromProgramRequest?: BodyType<TLRemedyTrBulkFromProgramRequest>,
) => {
  return [
    'POST',
    'code-remediation-v3',
    'remedies',
    remedyId,
    'bulk-transport-requests',
    tLRemedyTrBulkFromProgramRequest,
  ] as const;
};

export const getReconcileProgramFileSubobjectInsertParentTrViaAdtAsyncQueryOptions = <
  TData = Awaited<ReturnType<typeof reconcileProgramFileSubobjectInsertParentTrViaAdtAsync>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId }: ReconcileProgramFileSubobjectInsertParentTrViaAdtAsyncPathParameters,
  tLRemedyTrBulkFromProgramRequest: BodyType<TLRemedyTrBulkFromProgramRequest>,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof reconcileProgramFileSubobjectInsertParentTrViaAdtAsync>>, TError, TData>
    >;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getReconcileProgramFileSubobjectInsertParentTrViaAdtAsyncQueryKey({ remedyId }, tLRemedyTrBulkFromProgramRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof reconcileProgramFileSubobjectInsertParentTrViaAdtAsync>>> = ({
    signal,
  }) =>
    reconcileProgramFileSubobjectInsertParentTrViaAdtAsync(
      { remedyId },
      tLRemedyTrBulkFromProgramRequest,
      requestOptions,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: remedyId !== null && remedyId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof reconcileProgramFileSubobjectInsertParentTrViaAdtAsync>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ReconcileProgramFileSubobjectInsertParentTrViaAdtAsyncQueryResult = NonNullable<
  Awaited<ReturnType<typeof reconcileProgramFileSubobjectInsertParentTrViaAdtAsync>>
>;
export type ReconcileProgramFileSubobjectInsertParentTrViaAdtAsyncQueryError = ErrorType<TLHTTPValidationError>;

export function useReconcileProgramFileSubobjectInsertParentTrViaAdtAsync<
  TData = Awaited<ReturnType<typeof reconcileProgramFileSubobjectInsertParentTrViaAdtAsync>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ReconcileProgramFileSubobjectInsertParentTrViaAdtAsyncPathParameters,
  tLRemedyTrBulkFromProgramRequest: BodyType<TLRemedyTrBulkFromProgramRequest>,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof reconcileProgramFileSubobjectInsertParentTrViaAdtAsync>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof reconcileProgramFileSubobjectInsertParentTrViaAdtAsync>>,
          TError,
          Awaited<ReturnType<typeof reconcileProgramFileSubobjectInsertParentTrViaAdtAsync>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useReconcileProgramFileSubobjectInsertParentTrViaAdtAsync<
  TData = Awaited<ReturnType<typeof reconcileProgramFileSubobjectInsertParentTrViaAdtAsync>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ReconcileProgramFileSubobjectInsertParentTrViaAdtAsyncPathParameters,
  tLRemedyTrBulkFromProgramRequest: BodyType<TLRemedyTrBulkFromProgramRequest>,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof reconcileProgramFileSubobjectInsertParentTrViaAdtAsync>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof reconcileProgramFileSubobjectInsertParentTrViaAdtAsync>>,
          TError,
          Awaited<ReturnType<typeof reconcileProgramFileSubobjectInsertParentTrViaAdtAsync>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useReconcileProgramFileSubobjectInsertParentTrViaAdtAsync<
  TData = Awaited<ReturnType<typeof reconcileProgramFileSubobjectInsertParentTrViaAdtAsync>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ReconcileProgramFileSubobjectInsertParentTrViaAdtAsyncPathParameters,
  tLRemedyTrBulkFromProgramRequest: BodyType<TLRemedyTrBulkFromProgramRequest>,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof reconcileProgramFileSubobjectInsertParentTrViaAdtAsync>>, TError, TData>
    >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Reconcile program file: subobject insert + parent TR via ADT (async)
 */

export function useReconcileProgramFileSubobjectInsertParentTrViaAdtAsync<
  TData = Awaited<ReturnType<typeof reconcileProgramFileSubobjectInsertParentTrViaAdtAsync>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId }: ReconcileProgramFileSubobjectInsertParentTrViaAdtAsyncPathParameters,
  tLRemedyTrBulkFromProgramRequest: BodyType<TLRemedyTrBulkFromProgramRequest>,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof reconcileProgramFileSubobjectInsertParentTrViaAdtAsync>>, TError, TData>
    >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getReconcileProgramFileSubobjectInsertParentTrViaAdtAsyncQueryOptions(
    { remedyId },
    tLRemedyTrBulkFromProgramRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Reconcile program file: subobject insert + parent TR via ADT (async)
 */
export const prefetchReconcileProgramFileSubobjectInsertParentTrViaAdtAsyncQuery = async <
  TData = Awaited<ReturnType<typeof reconcileProgramFileSubobjectInsertParentTrViaAdtAsync>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { remedyId }: ReconcileProgramFileSubobjectInsertParentTrViaAdtAsyncPathParameters,
  tLRemedyTrBulkFromProgramRequest: BodyType<TLRemedyTrBulkFromProgramRequest>,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof reconcileProgramFileSubobjectInsertParentTrViaAdtAsync>>, TError, TData>
    >;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getReconcileProgramFileSubobjectInsertParentTrViaAdtAsyncQueryOptions(
    { remedyId },
    tLRemedyTrBulkFromProgramRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Reconcile program file: subobject insert + parent TR via ADT (async)
 */
export const invalidateReconcileProgramFileSubobjectInsertParentTrViaAdtAsync = async (
  queryClient: QueryClient,
  { remedyId }: ReconcileProgramFileSubobjectInsertParentTrViaAdtAsyncPathParameters,
  tLRemedyTrBulkFromProgramRequest: BodyType<TLRemedyTrBulkFromProgramRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    {
      queryKey: getReconcileProgramFileSubobjectInsertParentTrViaAdtAsyncQueryKey(
        { remedyId },
        tLRemedyTrBulkFromProgramRequest,
      ),
    },
    options,
  );

  return queryClient;
};

/**
 * @summary Check Active Code
 */
export const checkActiveCode = (
  { remedyId }: CheckActiveCodePathParameters,
  tLValidateCodeRequest: BodyType<TLValidateCodeRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLValidateCodeResponse>(
    {
      url: `/code-remediation-v3/remedies/${remedyId}/check-active-code`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLValidateCodeRequest,
      signal,
    },
    options,
  );
};

export const getCheckActiveCodeQueryKey = (
  { remedyId }: CheckActiveCodePathParameters,
  tLValidateCodeRequest?: BodyType<TLValidateCodeRequest>,
) => {
  return ['POST', 'code-remediation-v3', 'remedies', remedyId, 'check-active-code', tLValidateCodeRequest] as const;
};

export const getCheckActiveCodeQueryOptions = <
  TData = Awaited<ReturnType<typeof checkActiveCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId }: CheckActiveCodePathParameters,
  tLValidateCodeRequest: BodyType<TLValidateCodeRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof checkActiveCode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCheckActiveCodeQueryKey({ remedyId }, tLValidateCodeRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof checkActiveCode>>> = ({ signal }) =>
    checkActiveCode({ remedyId }, tLValidateCodeRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: remedyId !== null && remedyId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof checkActiveCode>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type CheckActiveCodeQueryResult = NonNullable<Awaited<ReturnType<typeof checkActiveCode>>>;
export type CheckActiveCodeQueryError = ErrorType<TLHTTPValidationError>;

export function useCheckActiveCode<
  TData = Awaited<ReturnType<typeof checkActiveCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CheckActiveCodePathParameters,
  tLValidateCodeRequest: BodyType<TLValidateCodeRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof checkActiveCode>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof checkActiveCode>>,
          TError,
          Awaited<ReturnType<typeof checkActiveCode>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCheckActiveCode<
  TData = Awaited<ReturnType<typeof checkActiveCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CheckActiveCodePathParameters,
  tLValidateCodeRequest: BodyType<TLValidateCodeRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof checkActiveCode>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof checkActiveCode>>,
          TError,
          Awaited<ReturnType<typeof checkActiveCode>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCheckActiveCode<
  TData = Awaited<ReturnType<typeof checkActiveCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CheckActiveCodePathParameters,
  tLValidateCodeRequest: BodyType<TLValidateCodeRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof checkActiveCode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Check Active Code
 */

export function useCheckActiveCode<
  TData = Awaited<ReturnType<typeof checkActiveCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId }: CheckActiveCodePathParameters,
  tLValidateCodeRequest: BodyType<TLValidateCodeRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof checkActiveCode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCheckActiveCodeQueryOptions({ remedyId }, tLValidateCodeRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Check Active Code
 */
export const prefetchCheckActiveCodeQuery = async <
  TData = Awaited<ReturnType<typeof checkActiveCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { remedyId }: CheckActiveCodePathParameters,
  tLValidateCodeRequest: BodyType<TLValidateCodeRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof checkActiveCode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCheckActiveCodeQueryOptions({ remedyId }, tLValidateCodeRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Check Active Code
 */
export const invalidateCheckActiveCode = async (
  queryClient: QueryClient,
  { remedyId }: CheckActiveCodePathParameters,
  tLValidateCodeRequest: BodyType<TLValidateCodeRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getCheckActiveCodeQueryKey({ remedyId }, tLValidateCodeRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Update Remedy
 */
export const updateRemedy = (
  { remedyId }: UpdateRemedyPathParameters,
  tLRemedyUpdateRequest: BodyType<TLRemedyUpdateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLRemedyResponse>(
    {
      url: `/code-remediation-v3/remedies/${remedyId}/details`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLRemedyUpdateRequest,
      signal,
    },
    options,
  );
};

export const getUpdateRemedyQueryKey = (
  { remedyId }: UpdateRemedyPathParameters,
  tLRemedyUpdateRequest?: BodyType<TLRemedyUpdateRequest>,
) => {
  return ['PATCH', 'code-remediation-v3', 'remedies', remedyId, 'details', tLRemedyUpdateRequest] as const;
};

export const getUpdateRemedyQueryOptions = <
  TData = Awaited<ReturnType<typeof updateRemedy>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId }: UpdateRemedyPathParameters,
  tLRemedyUpdateRequest: BodyType<TLRemedyUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateRemedy>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUpdateRemedyQueryKey({ remedyId }, tLRemedyUpdateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateRemedy>>> = ({ signal }) =>
    updateRemedy({ remedyId }, tLRemedyUpdateRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: remedyId !== null && remedyId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateRemedy>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateRemedyQueryResult = NonNullable<Awaited<ReturnType<typeof updateRemedy>>>;
export type UpdateRemedyQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateRemedy<
  TData = Awaited<ReturnType<typeof updateRemedy>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateRemedyPathParameters,
  tLRemedyUpdateRequest: BodyType<TLRemedyUpdateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateRemedy>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateRemedy>>,
          TError,
          Awaited<ReturnType<typeof updateRemedy>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateRemedy<
  TData = Awaited<ReturnType<typeof updateRemedy>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateRemedyPathParameters,
  tLRemedyUpdateRequest: BodyType<TLRemedyUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateRemedy>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateRemedy>>,
          TError,
          Awaited<ReturnType<typeof updateRemedy>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateRemedy<
  TData = Awaited<ReturnType<typeof updateRemedy>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateRemedyPathParameters,
  tLRemedyUpdateRequest: BodyType<TLRemedyUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateRemedy>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Remedy
 */

export function useUpdateRemedy<
  TData = Awaited<ReturnType<typeof updateRemedy>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId }: UpdateRemedyPathParameters,
  tLRemedyUpdateRequest: BodyType<TLRemedyUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateRemedy>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateRemedyQueryOptions({ remedyId }, tLRemedyUpdateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Remedy
 */
export const prefetchUpdateRemedyQuery = async <
  TData = Awaited<ReturnType<typeof updateRemedy>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { remedyId }: UpdateRemedyPathParameters,
  tLRemedyUpdateRequest: BodyType<TLRemedyUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateRemedy>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateRemedyQueryOptions({ remedyId }, tLRemedyUpdateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Remedy
 */
export const invalidateUpdateRemedy = async (
  queryClient: QueryClient,
  { remedyId }: UpdateRemedyPathParameters,
  tLRemedyUpdateRequest: BodyType<TLRemedyUpdateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateRemedyQueryKey({ remedyId }, tLRemedyUpdateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Remediate Fle
 */
export const remediateFle = (
  { remedyId }: RemediateFlePathParameters,
  tLFLERemediateRequest: BodyType<TLFLERemediateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLFleAnalyzeResponse>(
    {
      url: `/code-remediation-v3/remedies/${remedyId}/fle-remediate`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLFLERemediateRequest,
      signal,
    },
    options,
  );
};

export const getRemediateFleQueryKey = (
  { remedyId }: RemediateFlePathParameters,
  tLFLERemediateRequest?: BodyType<TLFLERemediateRequest>,
) => {
  return ['POST', 'code-remediation-v3', 'remedies', remedyId, 'fle-remediate', tLFLERemediateRequest] as const;
};

export const getRemediateFleQueryOptions = <
  TData = Awaited<ReturnType<typeof remediateFle>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId }: RemediateFlePathParameters,
  tLFLERemediateRequest: BodyType<TLFLERemediateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof remediateFle>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRemediateFleQueryKey({ remedyId }, tLFLERemediateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof remediateFle>>> = ({ signal }) =>
    remediateFle({ remedyId }, tLFLERemediateRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: remedyId !== null && remedyId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof remediateFle>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type RemediateFleQueryResult = NonNullable<Awaited<ReturnType<typeof remediateFle>>>;
export type RemediateFleQueryError = ErrorType<TLHTTPValidationError>;

export function useRemediateFle<
  TData = Awaited<ReturnType<typeof remediateFle>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RemediateFlePathParameters,
  tLFLERemediateRequest: BodyType<TLFLERemediateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof remediateFle>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof remediateFle>>,
          TError,
          Awaited<ReturnType<typeof remediateFle>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRemediateFle<
  TData = Awaited<ReturnType<typeof remediateFle>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RemediateFlePathParameters,
  tLFLERemediateRequest: BodyType<TLFLERemediateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof remediateFle>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof remediateFle>>,
          TError,
          Awaited<ReturnType<typeof remediateFle>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRemediateFle<
  TData = Awaited<ReturnType<typeof remediateFle>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RemediateFlePathParameters,
  tLFLERemediateRequest: BodyType<TLFLERemediateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof remediateFle>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Remediate Fle
 */

export function useRemediateFle<
  TData = Awaited<ReturnType<typeof remediateFle>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId }: RemediateFlePathParameters,
  tLFLERemediateRequest: BodyType<TLFLERemediateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof remediateFle>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRemediateFleQueryOptions({ remedyId }, tLFLERemediateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Remediate Fle
 */
export const prefetchRemediateFleQuery = async <
  TData = Awaited<ReturnType<typeof remediateFle>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { remedyId }: RemediateFlePathParameters,
  tLFLERemediateRequest: BodyType<TLFLERemediateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof remediateFle>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRemediateFleQueryOptions({ remedyId }, tLFLERemediateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Remediate Fle
 */
export const invalidateRemediateFle = async (
  queryClient: QueryClient,
  { remedyId }: RemediateFlePathParameters,
  tLFLERemediateRequest: BodyType<TLFLERemediateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getRemediateFleQueryKey({ remedyId }, tLFLERemediateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Source Code
 */
export const getSourceCode = (
  { remedyId }: GetSourceCodePathParameters,
  params: GetSourceCodeParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSourceCodeResponse>(
    { url: `/code-remediation-v3/remedies/${remedyId}/get-source-code`, method: 'GET', params, signal },
    options,
  );
};

export const getGetSourceCodeQueryKey = ({ remedyId }: GetSourceCodePathParameters, params?: GetSourceCodeParams) => {
  return ['code-remediation-v3', 'remedies', remedyId, 'get-source-code', ...(params ? [params] : [])] as const;
};

export const getGetSourceCodeQueryOptions = <
  TData = Awaited<ReturnType<typeof getSourceCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId }: GetSourceCodePathParameters,
  params: GetSourceCodeParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSourceCode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetSourceCodeQueryKey({ remedyId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getSourceCode>>> = ({ signal }) =>
    getSourceCode({ remedyId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: remedyId !== null && remedyId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getSourceCode>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetSourceCodeQueryResult = NonNullable<Awaited<ReturnType<typeof getSourceCode>>>;
export type GetSourceCodeQueryError = ErrorType<TLHTTPValidationError>;

export function useGetSourceCode<
  TData = Awaited<ReturnType<typeof getSourceCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetSourceCodePathParameters,
  params: GetSourceCodeParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSourceCode>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSourceCode>>,
          TError,
          Awaited<ReturnType<typeof getSourceCode>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSourceCode<
  TData = Awaited<ReturnType<typeof getSourceCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetSourceCodePathParameters,
  params: GetSourceCodeParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSourceCode>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSourceCode>>,
          TError,
          Awaited<ReturnType<typeof getSourceCode>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSourceCode<
  TData = Awaited<ReturnType<typeof getSourceCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetSourceCodePathParameters,
  params: GetSourceCodeParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSourceCode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Source Code
 */

export function useGetSourceCode<
  TData = Awaited<ReturnType<typeof getSourceCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId }: GetSourceCodePathParameters,
  params: GetSourceCodeParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSourceCode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetSourceCodeQueryOptions({ remedyId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Source Code
 */
export const prefetchGetSourceCodeQuery = async <
  TData = Awaited<ReturnType<typeof getSourceCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { remedyId }: GetSourceCodePathParameters,
  params: GetSourceCodeParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSourceCode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetSourceCodeQueryOptions({ remedyId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Source Code
 */
export const invalidateGetSourceCode = async (
  queryClient: QueryClient,
  { remedyId }: GetSourceCodePathParameters,
  params: GetSourceCodeParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetSourceCodeQueryKey({ remedyId }, params) }, options);

  return queryClient;
};

/**
 * @summary Create Mod Log
 */
export const createModLog = (
  { remedyId }: CreateModLogPathParameters,
  tLModLogGenerationRequest: BodyType<TLModLogGenerationRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLModLogGenerationResponse>(
    {
      url: `/code-remediation-v3/remedies/${remedyId}/mod-log`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLModLogGenerationRequest,
      signal,
    },
    options,
  );
};

export const getCreateModLogQueryKey = (
  { remedyId }: CreateModLogPathParameters,
  tLModLogGenerationRequest?: BodyType<TLModLogGenerationRequest>,
) => {
  return ['POST', 'code-remediation-v3', 'remedies', remedyId, 'mod-log', tLModLogGenerationRequest] as const;
};

export const getCreateModLogQueryOptions = <
  TData = Awaited<ReturnType<typeof createModLog>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId }: CreateModLogPathParameters,
  tLModLogGenerationRequest: BodyType<TLModLogGenerationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createModLog>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreateModLogQueryKey({ remedyId }, tLModLogGenerationRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createModLog>>> = ({ signal }) =>
    createModLog({ remedyId }, tLModLogGenerationRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: remedyId !== null && remedyId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof createModLog>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type CreateModLogQueryResult = NonNullable<Awaited<ReturnType<typeof createModLog>>>;
export type CreateModLogQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateModLog<
  TData = Awaited<ReturnType<typeof createModLog>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateModLogPathParameters,
  tLModLogGenerationRequest: BodyType<TLModLogGenerationRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createModLog>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createModLog>>,
          TError,
          Awaited<ReturnType<typeof createModLog>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateModLog<
  TData = Awaited<ReturnType<typeof createModLog>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateModLogPathParameters,
  tLModLogGenerationRequest: BodyType<TLModLogGenerationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createModLog>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createModLog>>,
          TError,
          Awaited<ReturnType<typeof createModLog>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateModLog<
  TData = Awaited<ReturnType<typeof createModLog>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateModLogPathParameters,
  tLModLogGenerationRequest: BodyType<TLModLogGenerationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createModLog>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Mod Log
 */

export function useCreateModLog<
  TData = Awaited<ReturnType<typeof createModLog>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId }: CreateModLogPathParameters,
  tLModLogGenerationRequest: BodyType<TLModLogGenerationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createModLog>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateModLogQueryOptions({ remedyId }, tLModLogGenerationRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Mod Log
 */
export const prefetchCreateModLogQuery = async <
  TData = Awaited<ReturnType<typeof createModLog>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { remedyId }: CreateModLogPathParameters,
  tLModLogGenerationRequest: BodyType<TLModLogGenerationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createModLog>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateModLogQueryOptions({ remedyId }, tLModLogGenerationRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Mod Log
 */
export const invalidateCreateModLog = async (
  queryClient: QueryClient,
  { remedyId }: CreateModLogPathParameters,
  tLModLogGenerationRequest: BodyType<TLModLogGenerationRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getCreateModLogQueryKey({ remedyId }, tLModLogGenerationRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Objects With Findings
 */
export const getObjectsWithFindings = (
  { remedyId }: GetObjectsWithFindingsPathParameters,
  tLObjectIdsRequest: BodyType<TLObjectIdsRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLObjectIdsResponse>(
    {
      url: `/code-remediation-v3/remedies/${remedyId}/objects-with-findings`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLObjectIdsRequest,
      signal,
    },
    options,
  );
};

export const getGetObjectsWithFindingsQueryKey = (
  { remedyId }: GetObjectsWithFindingsPathParameters,
  tLObjectIdsRequest?: BodyType<TLObjectIdsRequest>,
) => {
  return ['POST', 'code-remediation-v3', 'remedies', remedyId, 'objects-with-findings', tLObjectIdsRequest] as const;
};

export const getGetObjectsWithFindingsQueryOptions = <
  TData = Awaited<ReturnType<typeof getObjectsWithFindings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId }: GetObjectsWithFindingsPathParameters,
  tLObjectIdsRequest: BodyType<TLObjectIdsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectsWithFindings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetObjectsWithFindingsQueryKey({ remedyId }, tLObjectIdsRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getObjectsWithFindings>>> = ({ signal }) =>
    getObjectsWithFindings({ remedyId }, tLObjectIdsRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: remedyId !== null && remedyId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getObjectsWithFindings>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetObjectsWithFindingsQueryResult = NonNullable<Awaited<ReturnType<typeof getObjectsWithFindings>>>;
export type GetObjectsWithFindingsQueryError = ErrorType<TLHTTPValidationError>;

export function useGetObjectsWithFindings<
  TData = Awaited<ReturnType<typeof getObjectsWithFindings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetObjectsWithFindingsPathParameters,
  tLObjectIdsRequest: BodyType<TLObjectIdsRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectsWithFindings>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getObjectsWithFindings>>,
          TError,
          Awaited<ReturnType<typeof getObjectsWithFindings>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetObjectsWithFindings<
  TData = Awaited<ReturnType<typeof getObjectsWithFindings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetObjectsWithFindingsPathParameters,
  tLObjectIdsRequest: BodyType<TLObjectIdsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectsWithFindings>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getObjectsWithFindings>>,
          TError,
          Awaited<ReturnType<typeof getObjectsWithFindings>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetObjectsWithFindings<
  TData = Awaited<ReturnType<typeof getObjectsWithFindings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetObjectsWithFindingsPathParameters,
  tLObjectIdsRequest: BodyType<TLObjectIdsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectsWithFindings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Objects With Findings
 */

export function useGetObjectsWithFindings<
  TData = Awaited<ReturnType<typeof getObjectsWithFindings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId }: GetObjectsWithFindingsPathParameters,
  tLObjectIdsRequest: BodyType<TLObjectIdsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectsWithFindings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetObjectsWithFindingsQueryOptions({ remedyId }, tLObjectIdsRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Objects With Findings
 */
export const prefetchGetObjectsWithFindingsQuery = async <
  TData = Awaited<ReturnType<typeof getObjectsWithFindings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { remedyId }: GetObjectsWithFindingsPathParameters,
  tLObjectIdsRequest: BodyType<TLObjectIdsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectsWithFindings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetObjectsWithFindingsQueryOptions({ remedyId }, tLObjectIdsRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Objects With Findings
 */
export const invalidateGetObjectsWithFindings = async (
  queryClient: QueryClient,
  { remedyId }: GetObjectsWithFindingsPathParameters,
  tLObjectIdsRequest: BodyType<TLObjectIdsRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getGetObjectsWithFindingsQueryKey({ remedyId }, tLObjectIdsRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Scan
 */
export const scan = (
  { remedyId }: ScanPathParameters,
  tLScanRequest: BodyType<TLScanRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLScanResponse>(
    {
      url: `/code-remediation-v3/remedies/${remedyId}/scan`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLScanRequest,
      signal,
    },
    options,
  );
};

export const getScanQueryKey = ({ remedyId }: ScanPathParameters, tLScanRequest?: BodyType<TLScanRequest>) => {
  return ['POST', 'code-remediation-v3', 'remedies', remedyId, 'scan', tLScanRequest] as const;
};

export const getScanQueryOptions = <
  TData = Awaited<ReturnType<typeof scan>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId }: ScanPathParameters,
  tLScanRequest: BodyType<TLScanRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof scan>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getScanQueryKey({ remedyId }, tLScanRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof scan>>> = ({ signal }) =>
    scan({ remedyId }, tLScanRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: remedyId !== null && remedyId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof scan>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ScanQueryResult = NonNullable<Awaited<ReturnType<typeof scan>>>;
export type ScanQueryError = ErrorType<TLHTTPValidationError>;

export function useScan<TData = Awaited<ReturnType<typeof scan>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: ScanPathParameters,
  tLScanRequest: BodyType<TLScanRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof scan>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<Awaited<ReturnType<typeof scan>>, TError, Awaited<ReturnType<typeof scan>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useScan<TData = Awaited<ReturnType<typeof scan>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: ScanPathParameters,
  tLScanRequest: BodyType<TLScanRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof scan>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<Awaited<ReturnType<typeof scan>>, TError, Awaited<ReturnType<typeof scan>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useScan<TData = Awaited<ReturnType<typeof scan>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: ScanPathParameters,
  tLScanRequest: BodyType<TLScanRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof scan>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Scan
 */

export function useScan<TData = Awaited<ReturnType<typeof scan>>, TError = ErrorType<TLHTTPValidationError>>(
  { remedyId }: ScanPathParameters,
  tLScanRequest: BodyType<TLScanRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof scan>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getScanQueryOptions({ remedyId }, tLScanRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Scan
 */
export const prefetchScanQuery = async <
  TData = Awaited<ReturnType<typeof scan>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { remedyId }: ScanPathParameters,
  tLScanRequest: BodyType<TLScanRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof scan>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getScanQueryOptions({ remedyId }, tLScanRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Scan
 */
export const invalidateScan = async (
  queryClient: QueryClient,
  { remedyId }: ScanPathParameters,
  tLScanRequest: BodyType<TLScanRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getScanQueryKey({ remedyId }, tLScanRequest) }, options);

  return queryClient;
};

/**
 * @summary Perform Syntax Check
 */
export const performSyntaxCheck = (
  { remedyId }: PerformSyntaxCheckPathParameters,
  tLCodeRemediationV3SyntaxCheckRequest: BodyType<TLCodeRemediationV3SyntaxCheckRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCodeRemediationV3SyntaxCheckResponse>(
    {
      url: `/code-remediation-v3/remedies/${remedyId}/syntax-check`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLCodeRemediationV3SyntaxCheckRequest,
      signal,
    },
    options,
  );
};

export const getPerformSyntaxCheckQueryKey = (
  { remedyId }: PerformSyntaxCheckPathParameters,
  tLCodeRemediationV3SyntaxCheckRequest?: BodyType<TLCodeRemediationV3SyntaxCheckRequest>,
) => {
  return [
    'POST',
    'code-remediation-v3',
    'remedies',
    remedyId,
    'syntax-check',
    tLCodeRemediationV3SyntaxCheckRequest,
  ] as const;
};

export const getPerformSyntaxCheckQueryOptions = <
  TData = Awaited<ReturnType<typeof performSyntaxCheck>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId }: PerformSyntaxCheckPathParameters,
  tLCodeRemediationV3SyntaxCheckRequest: BodyType<TLCodeRemediationV3SyntaxCheckRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof performSyntaxCheck>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getPerformSyntaxCheckQueryKey({ remedyId }, tLCodeRemediationV3SyntaxCheckRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof performSyntaxCheck>>> = ({ signal }) =>
    performSyntaxCheck({ remedyId }, tLCodeRemediationV3SyntaxCheckRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: remedyId !== null && remedyId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof performSyntaxCheck>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type PerformSyntaxCheckQueryResult = NonNullable<Awaited<ReturnType<typeof performSyntaxCheck>>>;
export type PerformSyntaxCheckQueryError = ErrorType<TLHTTPValidationError>;

export function usePerformSyntaxCheck<
  TData = Awaited<ReturnType<typeof performSyntaxCheck>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PerformSyntaxCheckPathParameters,
  tLCodeRemediationV3SyntaxCheckRequest: BodyType<TLCodeRemediationV3SyntaxCheckRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof performSyntaxCheck>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof performSyntaxCheck>>,
          TError,
          Awaited<ReturnType<typeof performSyntaxCheck>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePerformSyntaxCheck<
  TData = Awaited<ReturnType<typeof performSyntaxCheck>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PerformSyntaxCheckPathParameters,
  tLCodeRemediationV3SyntaxCheckRequest: BodyType<TLCodeRemediationV3SyntaxCheckRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof performSyntaxCheck>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof performSyntaxCheck>>,
          TError,
          Awaited<ReturnType<typeof performSyntaxCheck>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePerformSyntaxCheck<
  TData = Awaited<ReturnType<typeof performSyntaxCheck>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PerformSyntaxCheckPathParameters,
  tLCodeRemediationV3SyntaxCheckRequest: BodyType<TLCodeRemediationV3SyntaxCheckRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof performSyntaxCheck>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Perform Syntax Check
 */

export function usePerformSyntaxCheck<
  TData = Awaited<ReturnType<typeof performSyntaxCheck>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId }: PerformSyntaxCheckPathParameters,
  tLCodeRemediationV3SyntaxCheckRequest: BodyType<TLCodeRemediationV3SyntaxCheckRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof performSyntaxCheck>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getPerformSyntaxCheckQueryOptions({ remedyId }, tLCodeRemediationV3SyntaxCheckRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Perform Syntax Check
 */
export const prefetchPerformSyntaxCheckQuery = async <
  TData = Awaited<ReturnType<typeof performSyntaxCheck>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { remedyId }: PerformSyntaxCheckPathParameters,
  tLCodeRemediationV3SyntaxCheckRequest: BodyType<TLCodeRemediationV3SyntaxCheckRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof performSyntaxCheck>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getPerformSyntaxCheckQueryOptions({ remedyId }, tLCodeRemediationV3SyntaxCheckRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Perform Syntax Check
 */
export const invalidatePerformSyntaxCheck = async (
  queryClient: QueryClient,
  { remedyId }: PerformSyntaxCheckPathParameters,
  tLCodeRemediationV3SyntaxCheckRequest: BodyType<TLCodeRemediationV3SyntaxCheckRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getPerformSyntaxCheckQueryKey({ remedyId }, tLCodeRemediationV3SyntaxCheckRequest) },
    options,
  );

  return queryClient;
};

/**
 * Update source code for an object in a remedy.
 * @summary Update Source Code
 */
export const updateSourceCode = (
  { remedyId }: UpdateSourceCodePathParameters,
  tLUpdateSourceCodeRequest: BodyType<TLUpdateSourceCodeRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLUpdateSourceCodeResponse>(
    {
      url: `/code-remediation-v3/remedies/${remedyId}/update-source-code`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLUpdateSourceCodeRequest,
      signal,
    },
    options,
  );
};

export const getUpdateSourceCodeQueryKey = (
  { remedyId }: UpdateSourceCodePathParameters,
  tLUpdateSourceCodeRequest?: BodyType<TLUpdateSourceCodeRequest>,
) => {
  return [
    'POST',
    'code-remediation-v3',
    'remedies',
    remedyId,
    'update-source-code',
    tLUpdateSourceCodeRequest,
  ] as const;
};

export const getUpdateSourceCodeQueryOptions = <
  TData = Awaited<ReturnType<typeof updateSourceCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId }: UpdateSourceCodePathParameters,
  tLUpdateSourceCodeRequest: BodyType<TLUpdateSourceCodeRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateSourceCode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUpdateSourceCodeQueryKey({ remedyId }, tLUpdateSourceCodeRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateSourceCode>>> = ({ signal }) =>
    updateSourceCode({ remedyId }, tLUpdateSourceCodeRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: remedyId !== null && remedyId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateSourceCode>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateSourceCodeQueryResult = NonNullable<Awaited<ReturnType<typeof updateSourceCode>>>;
export type UpdateSourceCodeQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateSourceCode<
  TData = Awaited<ReturnType<typeof updateSourceCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateSourceCodePathParameters,
  tLUpdateSourceCodeRequest: BodyType<TLUpdateSourceCodeRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateSourceCode>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateSourceCode>>,
          TError,
          Awaited<ReturnType<typeof updateSourceCode>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateSourceCode<
  TData = Awaited<ReturnType<typeof updateSourceCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateSourceCodePathParameters,
  tLUpdateSourceCodeRequest: BodyType<TLUpdateSourceCodeRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateSourceCode>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateSourceCode>>,
          TError,
          Awaited<ReturnType<typeof updateSourceCode>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateSourceCode<
  TData = Awaited<ReturnType<typeof updateSourceCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateSourceCodePathParameters,
  tLUpdateSourceCodeRequest: BodyType<TLUpdateSourceCodeRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateSourceCode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Source Code
 */

export function useUpdateSourceCode<
  TData = Awaited<ReturnType<typeof updateSourceCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId }: UpdateSourceCodePathParameters,
  tLUpdateSourceCodeRequest: BodyType<TLUpdateSourceCodeRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateSourceCode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateSourceCodeQueryOptions({ remedyId }, tLUpdateSourceCodeRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Source Code
 */
export const prefetchUpdateSourceCodeQuery = async <
  TData = Awaited<ReturnType<typeof updateSourceCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { remedyId }: UpdateSourceCodePathParameters,
  tLUpdateSourceCodeRequest: BodyType<TLUpdateSourceCodeRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateSourceCode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateSourceCodeQueryOptions({ remedyId }, tLUpdateSourceCodeRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Source Code
 */
export const invalidateUpdateSourceCode = async (
  queryClient: QueryClient,
  { remedyId }: UpdateSourceCodePathParameters,
  tLUpdateSourceCodeRequest: BodyType<TLUpdateSourceCodeRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateSourceCodeQueryKey({ remedyId }, tLUpdateSourceCodeRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Related Objects
 */
export const getRelatedObjects = (
  { remedyId, objectId }: GetRelatedObjectsPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLRemedyObjectsListResponse>(
    { url: `/code-remediation-v3/remedies/${remedyId}/${objectId}`, method: 'GET', signal },
    options,
  );
};

export const getGetRelatedObjectsQueryKey = ({ remedyId, objectId }: GetRelatedObjectsPathParameters) => {
  return ['code-remediation-v3', 'remedies', remedyId, objectId] as const;
};

export const getGetRelatedObjectsQueryOptions = <
  TData = Awaited<ReturnType<typeof getRelatedObjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId, objectId }: GetRelatedObjectsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRelatedObjects>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetRelatedObjectsQueryKey({ remedyId, objectId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getRelatedObjects>>> = ({ signal }) =>
    getRelatedObjects({ remedyId, objectId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: remedyId !== null && remedyId !== undefined && objectId !== null && objectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getRelatedObjects>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetRelatedObjectsQueryResult = NonNullable<Awaited<ReturnType<typeof getRelatedObjects>>>;
export type GetRelatedObjectsQueryError = ErrorType<TLHTTPValidationError>;

export function useGetRelatedObjects<
  TData = Awaited<ReturnType<typeof getRelatedObjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetRelatedObjectsPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRelatedObjects>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getRelatedObjects>>,
          TError,
          Awaited<ReturnType<typeof getRelatedObjects>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetRelatedObjects<
  TData = Awaited<ReturnType<typeof getRelatedObjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetRelatedObjectsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRelatedObjects>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getRelatedObjects>>,
          TError,
          Awaited<ReturnType<typeof getRelatedObjects>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetRelatedObjects<
  TData = Awaited<ReturnType<typeof getRelatedObjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetRelatedObjectsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRelatedObjects>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Related Objects
 */

export function useGetRelatedObjects<
  TData = Awaited<ReturnType<typeof getRelatedObjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId, objectId }: GetRelatedObjectsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRelatedObjects>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetRelatedObjectsQueryOptions({ remedyId, objectId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Related Objects
 */
export const prefetchGetRelatedObjectsQuery = async <
  TData = Awaited<ReturnType<typeof getRelatedObjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { remedyId, objectId }: GetRelatedObjectsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRelatedObjects>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetRelatedObjectsQueryOptions({ remedyId, objectId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Related Objects
 */
export const invalidateGetRelatedObjects = async (
  queryClient: QueryClient,
  { remedyId, objectId }: GetRelatedObjectsPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetRelatedObjectsQueryKey({ remedyId, objectId }) }, options);

  return queryClient;
};

/**
 * @summary Update Object Status
 */
export const updateObjectStatus = (
  { remedyId, objectId }: UpdateObjectStatusPathParameters,
  params: UpdateObjectStatusParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLUpdateStatusResponse>(
    { url: `/code-remediation-v3/remedies/${remedyId}/${objectId}/status`, method: 'PATCH', params, signal },
    options,
  );
};

export const getUpdateObjectStatusQueryKey = (
  { remedyId, objectId }: UpdateObjectStatusPathParameters,
  params?: UpdateObjectStatusParams,
) => {
  return [
    'PATCH',
    'code-remediation-v3',
    'remedies',
    remedyId,
    objectId,
    'status',
    ...(params ? [params] : []),
  ] as const;
};

export const getUpdateObjectStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof updateObjectStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId, objectId }: UpdateObjectStatusPathParameters,
  params: UpdateObjectStatusParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateObjectStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUpdateObjectStatusQueryKey({ remedyId, objectId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateObjectStatus>>> = ({ signal }) =>
    updateObjectStatus({ remedyId, objectId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: remedyId !== null && remedyId !== undefined && objectId !== null && objectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateObjectStatus>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateObjectStatusQueryResult = NonNullable<Awaited<ReturnType<typeof updateObjectStatus>>>;
export type UpdateObjectStatusQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateObjectStatus<
  TData = Awaited<ReturnType<typeof updateObjectStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateObjectStatusPathParameters,
  params: UpdateObjectStatusParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateObjectStatus>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateObjectStatus>>,
          TError,
          Awaited<ReturnType<typeof updateObjectStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateObjectStatus<
  TData = Awaited<ReturnType<typeof updateObjectStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateObjectStatusPathParameters,
  params: UpdateObjectStatusParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateObjectStatus>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateObjectStatus>>,
          TError,
          Awaited<ReturnType<typeof updateObjectStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateObjectStatus<
  TData = Awaited<ReturnType<typeof updateObjectStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateObjectStatusPathParameters,
  params: UpdateObjectStatusParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateObjectStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Object Status
 */

export function useUpdateObjectStatus<
  TData = Awaited<ReturnType<typeof updateObjectStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { remedyId, objectId }: UpdateObjectStatusPathParameters,
  params: UpdateObjectStatusParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateObjectStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateObjectStatusQueryOptions({ remedyId, objectId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Object Status
 */
export const prefetchUpdateObjectStatusQuery = async <
  TData = Awaited<ReturnType<typeof updateObjectStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { remedyId, objectId }: UpdateObjectStatusPathParameters,
  params: UpdateObjectStatusParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateObjectStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateObjectStatusQueryOptions({ remedyId, objectId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Object Status
 */
export const invalidateUpdateObjectStatus = async (
  queryClient: QueryClient,
  { remedyId, objectId }: UpdateObjectStatusPathParameters,
  params: UpdateObjectStatusParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateObjectStatusQueryKey({ remedyId, objectId }, params) },
    options,
  );

  return queryClient;
};

/**
 * @summary Create Remediation Sap Object Import
 */
export const createRemediationSapObjectImport = (
  tLRemediationObjectImportRequest: BodyType<TLRemediationObjectImportRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLRemedyCreateResponse>(
    {
      url: `/code-remediation-v3/sap-object-imports`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLRemediationObjectImportRequest,
      signal,
    },
    options,
  );
};

export const getCreateRemediationSapObjectImportQueryKey = (
  tLRemediationObjectImportRequest?: BodyType<TLRemediationObjectImportRequest>,
) => {
  return ['POST', 'code-remediation-v3', 'sap-object-imports', tLRemediationObjectImportRequest] as const;
};

export const getCreateRemediationSapObjectImportQueryOptions = <
  TData = Awaited<ReturnType<typeof createRemediationSapObjectImport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLRemediationObjectImportRequest: BodyType<TLRemediationObjectImportRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createRemediationSapObjectImport>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getCreateRemediationSapObjectImportQueryKey(tLRemediationObjectImportRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createRemediationSapObjectImport>>> = ({ signal }) =>
    createRemediationSapObjectImport(tLRemediationObjectImportRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createRemediationSapObjectImport>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateRemediationSapObjectImportQueryResult = NonNullable<
  Awaited<ReturnType<typeof createRemediationSapObjectImport>>
>;
export type CreateRemediationSapObjectImportQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateRemediationSapObjectImport<
  TData = Awaited<ReturnType<typeof createRemediationSapObjectImport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLRemediationObjectImportRequest: BodyType<TLRemediationObjectImportRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createRemediationSapObjectImport>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createRemediationSapObjectImport>>,
          TError,
          Awaited<ReturnType<typeof createRemediationSapObjectImport>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateRemediationSapObjectImport<
  TData = Awaited<ReturnType<typeof createRemediationSapObjectImport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLRemediationObjectImportRequest: BodyType<TLRemediationObjectImportRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createRemediationSapObjectImport>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createRemediationSapObjectImport>>,
          TError,
          Awaited<ReturnType<typeof createRemediationSapObjectImport>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateRemediationSapObjectImport<
  TData = Awaited<ReturnType<typeof createRemediationSapObjectImport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLRemediationObjectImportRequest: BodyType<TLRemediationObjectImportRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createRemediationSapObjectImport>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Remediation Sap Object Import
 */

export function useCreateRemediationSapObjectImport<
  TData = Awaited<ReturnType<typeof createRemediationSapObjectImport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLRemediationObjectImportRequest: BodyType<TLRemediationObjectImportRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createRemediationSapObjectImport>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateRemediationSapObjectImportQueryOptions(tLRemediationObjectImportRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Remediation Sap Object Import
 */
export const prefetchCreateRemediationSapObjectImportQuery = async <
  TData = Awaited<ReturnType<typeof createRemediationSapObjectImport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLRemediationObjectImportRequest: BodyType<TLRemediationObjectImportRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createRemediationSapObjectImport>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateRemediationSapObjectImportQueryOptions(tLRemediationObjectImportRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Remediation Sap Object Import
 */
export const invalidateCreateRemediationSapObjectImport = async (
  queryClient: QueryClient,
  tLRemediationObjectImportRequest: BodyType<TLRemediationObjectImportRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getCreateRemediationSapObjectImportQueryKey(tLRemediationObjectImportRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary List Remediation Sap Objects
 */
export const listRemediationSapObjects = (
  params: ListRemediationSapObjectsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLRemediationObjectSearchResponse>(
    { url: `/code-remediation-v3/sap-objects`, method: 'GET', params, signal },
    options,
  );
};

export const getListRemediationSapObjectsQueryKey = (params?: ListRemediationSapObjectsParams) => {
  return ['code-remediation-v3', 'sap-objects', ...(params ? [params] : [])] as const;
};

export const getListRemediationSapObjectsQueryOptions = <
  TData = Awaited<ReturnType<typeof listRemediationSapObjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: ListRemediationSapObjectsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listRemediationSapObjects>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListRemediationSapObjectsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listRemediationSapObjects>>> = ({ signal }) =>
    listRemediationSapObjects(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listRemediationSapObjects>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListRemediationSapObjectsQueryResult = NonNullable<Awaited<ReturnType<typeof listRemediationSapObjects>>>;
export type ListRemediationSapObjectsQueryError = ErrorType<TLHTTPValidationError>;

export function useListRemediationSapObjects<
  TData = Awaited<ReturnType<typeof listRemediationSapObjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: ListRemediationSapObjectsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listRemediationSapObjects>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listRemediationSapObjects>>,
          TError,
          Awaited<ReturnType<typeof listRemediationSapObjects>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListRemediationSapObjects<
  TData = Awaited<ReturnType<typeof listRemediationSapObjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: ListRemediationSapObjectsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listRemediationSapObjects>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listRemediationSapObjects>>,
          TError,
          Awaited<ReturnType<typeof listRemediationSapObjects>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListRemediationSapObjects<
  TData = Awaited<ReturnType<typeof listRemediationSapObjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: ListRemediationSapObjectsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listRemediationSapObjects>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Remediation Sap Objects
 */

export function useListRemediationSapObjects<
  TData = Awaited<ReturnType<typeof listRemediationSapObjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: ListRemediationSapObjectsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listRemediationSapObjects>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListRemediationSapObjectsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Remediation Sap Objects
 */
export const prefetchListRemediationSapObjectsQuery = async <
  TData = Awaited<ReturnType<typeof listRemediationSapObjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params: ListRemediationSapObjectsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listRemediationSapObjects>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListRemediationSapObjectsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Remediation Sap Objects
 */
export const invalidateListRemediationSapObjects = async (
  queryClient: QueryClient,
  params: ListRemediationSapObjectsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListRemediationSapObjectsQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Cancel Scans
 */
export const cancelScans = (
  tLCancelScansRequest: BodyType<TLCancelScansRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    {
      url: `/code-remediation-v3/scans/cancel`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLCancelScansRequest,
      signal,
    },
    options,
  );
};

export const getCancelScansQueryKey = (tLCancelScansRequest?: BodyType<TLCancelScansRequest>) => {
  return ['PATCH', 'code-remediation-v3', 'scans', 'cancel', tLCancelScansRequest] as const;
};

export const getCancelScansQueryOptions = <
  TData = Awaited<ReturnType<typeof cancelScans>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCancelScansRequest: BodyType<TLCancelScansRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelScans>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCancelScansQueryKey(tLCancelScansRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof cancelScans>>> = ({ signal }) =>
    cancelScans(tLCancelScansRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof cancelScans>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CancelScansQueryResult = NonNullable<Awaited<ReturnType<typeof cancelScans>>>;
export type CancelScansQueryError = ErrorType<TLHTTPValidationError>;

export function useCancelScans<
  TData = Awaited<ReturnType<typeof cancelScans>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCancelScansRequest: BodyType<TLCancelScansRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelScans>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof cancelScans>>,
          TError,
          Awaited<ReturnType<typeof cancelScans>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCancelScans<
  TData = Awaited<ReturnType<typeof cancelScans>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCancelScansRequest: BodyType<TLCancelScansRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelScans>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof cancelScans>>,
          TError,
          Awaited<ReturnType<typeof cancelScans>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCancelScans<
  TData = Awaited<ReturnType<typeof cancelScans>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCancelScansRequest: BodyType<TLCancelScansRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelScans>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Cancel Scans
 */

export function useCancelScans<
  TData = Awaited<ReturnType<typeof cancelScans>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCancelScansRequest: BodyType<TLCancelScansRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelScans>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCancelScansQueryOptions(tLCancelScansRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Cancel Scans
 */
export const prefetchCancelScansQuery = async <
  TData = Awaited<ReturnType<typeof cancelScans>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLCancelScansRequest: BodyType<TLCancelScansRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelScans>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCancelScansQueryOptions(tLCancelScansRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Cancel Scans
 */
export const invalidateCancelScans = async (
  queryClient: QueryClient,
  tLCancelScansRequest: BodyType<TLCancelScansRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getCancelScansQueryKey(tLCancelScansRequest) }, options);

  return queryClient;
};

/**
 * @summary Get Finding Metadata
 */
export const getFindingMetadata = (
  { scanId, findingId }: GetFindingMetadataPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLFindingMetadataResponse>(
    { url: `/code-remediation-v3/scans/${scanId}/findings/${findingId}/metadata`, method: 'GET', signal },
    options,
  );
};

export const getGetFindingMetadataQueryKey = ({ scanId, findingId }: GetFindingMetadataPathParameters) => {
  return ['code-remediation-v3', 'scans', scanId, 'findings', findingId, 'metadata'] as const;
};

export const getGetFindingMetadataQueryOptions = <
  TData = Awaited<ReturnType<typeof getFindingMetadata>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { scanId, findingId }: GetFindingMetadataPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFindingMetadata>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetFindingMetadataQueryKey({ scanId, findingId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getFindingMetadata>>> = ({ signal }) =>
    getFindingMetadata({ scanId, findingId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: scanId !== null && scanId !== undefined && findingId !== null && findingId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getFindingMetadata>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetFindingMetadataQueryResult = NonNullable<Awaited<ReturnType<typeof getFindingMetadata>>>;
export type GetFindingMetadataQueryError = ErrorType<TLHTTPValidationError>;

export function useGetFindingMetadata<
  TData = Awaited<ReturnType<typeof getFindingMetadata>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetFindingMetadataPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFindingMetadata>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFindingMetadata>>,
          TError,
          Awaited<ReturnType<typeof getFindingMetadata>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetFindingMetadata<
  TData = Awaited<ReturnType<typeof getFindingMetadata>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetFindingMetadataPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFindingMetadata>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFindingMetadata>>,
          TError,
          Awaited<ReturnType<typeof getFindingMetadata>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetFindingMetadata<
  TData = Awaited<ReturnType<typeof getFindingMetadata>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetFindingMetadataPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFindingMetadata>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Finding Metadata
 */

export function useGetFindingMetadata<
  TData = Awaited<ReturnType<typeof getFindingMetadata>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { scanId, findingId }: GetFindingMetadataPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFindingMetadata>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetFindingMetadataQueryOptions({ scanId, findingId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Finding Metadata
 */
export const prefetchGetFindingMetadataQuery = async <
  TData = Awaited<ReturnType<typeof getFindingMetadata>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { scanId, findingId }: GetFindingMetadataPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFindingMetadata>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetFindingMetadataQueryOptions({ scanId, findingId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Finding Metadata
 */
export const invalidateGetFindingMetadata = async (
  queryClient: QueryClient,
  { scanId, findingId }: GetFindingMetadataPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetFindingMetadataQueryKey({ scanId, findingId }) }, options);

  return queryClient;
};

/**
 * Get object details by processing CSV content from program ID.
 * @summary Create Scope Objects
 */
export const createScopeObjects = (
  tLObjectDetailsRequest: BodyType<TLObjectDetailsRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLRemedyCreateResponse>(
    {
      url: `/code-remediation-v3/start`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLObjectDetailsRequest,
      signal,
    },
    options,
  );
};

export const getCreateScopeObjectsQueryKey = (tLObjectDetailsRequest?: BodyType<TLObjectDetailsRequest>) => {
  return ['POST', 'code-remediation-v3', 'start', tLObjectDetailsRequest] as const;
};

export const getCreateScopeObjectsQueryOptions = <
  TData = Awaited<ReturnType<typeof createScopeObjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLObjectDetailsRequest: BodyType<TLObjectDetailsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createScopeObjects>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreateScopeObjectsQueryKey(tLObjectDetailsRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createScopeObjects>>> = ({ signal }) =>
    createScopeObjects(tLObjectDetailsRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createScopeObjects>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateScopeObjectsQueryResult = NonNullable<Awaited<ReturnType<typeof createScopeObjects>>>;
export type CreateScopeObjectsQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateScopeObjects<
  TData = Awaited<ReturnType<typeof createScopeObjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLObjectDetailsRequest: BodyType<TLObjectDetailsRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createScopeObjects>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createScopeObjects>>,
          TError,
          Awaited<ReturnType<typeof createScopeObjects>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateScopeObjects<
  TData = Awaited<ReturnType<typeof createScopeObjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLObjectDetailsRequest: BodyType<TLObjectDetailsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createScopeObjects>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createScopeObjects>>,
          TError,
          Awaited<ReturnType<typeof createScopeObjects>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateScopeObjects<
  TData = Awaited<ReturnType<typeof createScopeObjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLObjectDetailsRequest: BodyType<TLObjectDetailsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createScopeObjects>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Scope Objects
 */

export function useCreateScopeObjects<
  TData = Awaited<ReturnType<typeof createScopeObjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLObjectDetailsRequest: BodyType<TLObjectDetailsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createScopeObjects>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateScopeObjectsQueryOptions(tLObjectDetailsRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Scope Objects
 */
export const prefetchCreateScopeObjectsQuery = async <
  TData = Awaited<ReturnType<typeof createScopeObjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLObjectDetailsRequest: BodyType<TLObjectDetailsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createScopeObjects>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateScopeObjectsQueryOptions(tLObjectDetailsRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Scope Objects
 */
export const invalidateCreateScopeObjects = async (
  queryClient: QueryClient,
  tLObjectDetailsRequest: BodyType<TLObjectDetailsRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getCreateScopeObjectsQueryKey(tLObjectDetailsRequest) }, options);

  return queryClient;
};

/**
 * @summary Create Transport Import
 */
export const createTransportImport = (
  tLTransportImportCreateRequest: BodyType<TLTransportImportCreateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLRemedyCreateResponse>(
    {
      url: `/code-remediation-v3/transport-imports`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLTransportImportCreateRequest,
      signal,
    },
    options,
  );
};

export const getCreateTransportImportQueryKey = (
  tLTransportImportCreateRequest?: BodyType<TLTransportImportCreateRequest>,
) => {
  return ['POST', 'code-remediation-v3', 'transport-imports', tLTransportImportCreateRequest] as const;
};

export const getCreateTransportImportQueryOptions = <
  TData = Awaited<ReturnType<typeof createTransportImport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLTransportImportCreateRequest: BodyType<TLTransportImportCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createTransportImport>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreateTransportImportQueryKey(tLTransportImportCreateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createTransportImport>>> = ({ signal }) =>
    createTransportImport(tLTransportImportCreateRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createTransportImport>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateTransportImportQueryResult = NonNullable<Awaited<ReturnType<typeof createTransportImport>>>;
export type CreateTransportImportQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateTransportImport<
  TData = Awaited<ReturnType<typeof createTransportImport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLTransportImportCreateRequest: BodyType<TLTransportImportCreateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createTransportImport>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createTransportImport>>,
          TError,
          Awaited<ReturnType<typeof createTransportImport>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateTransportImport<
  TData = Awaited<ReturnType<typeof createTransportImport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLTransportImportCreateRequest: BodyType<TLTransportImportCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createTransportImport>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createTransportImport>>,
          TError,
          Awaited<ReturnType<typeof createTransportImport>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateTransportImport<
  TData = Awaited<ReturnType<typeof createTransportImport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLTransportImportCreateRequest: BodyType<TLTransportImportCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createTransportImport>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Transport Import
 */

export function useCreateTransportImport<
  TData = Awaited<ReturnType<typeof createTransportImport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLTransportImportCreateRequest: BodyType<TLTransportImportCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createTransportImport>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateTransportImportQueryOptions(tLTransportImportCreateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Transport Import
 */
export const prefetchCreateTransportImportQuery = async <
  TData = Awaited<ReturnType<typeof createTransportImport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLTransportImportCreateRequest: BodyType<TLTransportImportCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createTransportImport>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateTransportImportQueryOptions(tLTransportImportCreateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Transport Import
 */
export const invalidateCreateTransportImport = async (
  queryClient: QueryClient,
  tLTransportImportCreateRequest: BodyType<TLTransportImportCreateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getCreateTransportImportQueryKey(tLTransportImportCreateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Preview Transport Import
 */
export const previewTransportImport = (
  tLTransportImportPreviewRequest: BodyType<TLTransportImportPreviewRequest>,
  params?: PreviewTransportImportParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLTransportImportPreviewResponse>(
    {
      url: `/code-remediation-v3/transport-imports/preview`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLTransportImportPreviewRequest,
      params,
      signal,
    },
    options,
  );
};

export const getPreviewTransportImportQueryKey = (
  tLTransportImportPreviewRequest?: BodyType<TLTransportImportPreviewRequest>,
  params?: PreviewTransportImportParams,
) => {
  return [
    'POST',
    'code-remediation-v3',
    'transport-imports',
    'preview',
    ...(params ? [params] : []),
    tLTransportImportPreviewRequest,
  ] as const;
};

export const getPreviewTransportImportQueryOptions = <
  TData = Awaited<ReturnType<typeof previewTransportImport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLTransportImportPreviewRequest: BodyType<TLTransportImportPreviewRequest>,
  params?: PreviewTransportImportParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewTransportImport>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getPreviewTransportImportQueryKey(tLTransportImportPreviewRequest, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof previewTransportImport>>> = ({ signal }) =>
    previewTransportImport(tLTransportImportPreviewRequest, params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof previewTransportImport>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PreviewTransportImportQueryResult = NonNullable<Awaited<ReturnType<typeof previewTransportImport>>>;
export type PreviewTransportImportQueryError = ErrorType<TLHTTPValidationError>;

export function usePreviewTransportImport<
  TData = Awaited<ReturnType<typeof previewTransportImport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLTransportImportPreviewRequest: BodyType<TLTransportImportPreviewRequest>,
  params: undefined | PreviewTransportImportParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewTransportImport>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof previewTransportImport>>,
          TError,
          Awaited<ReturnType<typeof previewTransportImport>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePreviewTransportImport<
  TData = Awaited<ReturnType<typeof previewTransportImport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLTransportImportPreviewRequest: BodyType<TLTransportImportPreviewRequest>,
  params?: PreviewTransportImportParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewTransportImport>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof previewTransportImport>>,
          TError,
          Awaited<ReturnType<typeof previewTransportImport>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePreviewTransportImport<
  TData = Awaited<ReturnType<typeof previewTransportImport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLTransportImportPreviewRequest: BodyType<TLTransportImportPreviewRequest>,
  params?: PreviewTransportImportParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewTransportImport>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Preview Transport Import
 */

export function usePreviewTransportImport<
  TData = Awaited<ReturnType<typeof previewTransportImport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLTransportImportPreviewRequest: BodyType<TLTransportImportPreviewRequest>,
  params?: PreviewTransportImportParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewTransportImport>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getPreviewTransportImportQueryOptions(tLTransportImportPreviewRequest, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Preview Transport Import
 */
export const prefetchPreviewTransportImportQuery = async <
  TData = Awaited<ReturnType<typeof previewTransportImport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLTransportImportPreviewRequest: BodyType<TLTransportImportPreviewRequest>,
  params?: PreviewTransportImportParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewTransportImport>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getPreviewTransportImportQueryOptions(tLTransportImportPreviewRequest, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Preview Transport Import
 */
export const invalidatePreviewTransportImport = async (
  queryClient: QueryClient,
  tLTransportImportPreviewRequest: BodyType<TLTransportImportPreviewRequest>,
  params?: PreviewTransportImportParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getPreviewTransportImportQueryKey(tLTransportImportPreviewRequest, params) },
    options,
  );

  return queryClient;
};
